import React, { useState, useEffect } from 'react';
import { Users, Plus, Mail, Phone, Building2, MapPin, Briefcase, RefreshCw, Cloud, CheckCircle, Trash2, Linkedin, Search, Link, Loader2, ExternalLink, GraduationCap, Globe, Edit, X, Pencil, Check, GripVertical, Camera, Settings, ChevronUp, ChevronDown, Send, LayoutGrid, List, Download } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { apiCall } from '../services/api';
import { toast } from 'sonner';
import { useNylasAccounts, useCategoryTabs } from '../hooks';
import { useUserId } from '../context/UserAuthContext';
import { useDemoMode } from '../context/DemoContext';

const RealEstateManagingPartnerContacts = () => {
  // User ID from auth context - dynamic based on logged-in user
  const userId = useUserId();
  
  // Demo mode support
  const { isDemoMode, demoData, triggerViewOnlyWarning } = useDemoMode();
  
  // Main tab state: 'personal' or 'leads'
  const [mainContactTab, setMainContactTab] = useState('personal');
  
  // Lead contacts state
  const [leadContacts, setLeadContacts] = useState([]);
  const [leadCompanies, setLeadCompanies] = useState([]); // Store full company data
  const [loadingLeadContacts, setLoadingLeadContacts] = useState(false);
  const [leadContactsSearchQuery, setLeadContactsSearchQuery] = useState('');
  const [selectedLeadCategory, setSelectedLeadCategory] = useState('all');
  const [leadContactsSortBy, setLeadContactsSortBy] = useState('company-az'); // Sort options: company-az, company-za, contact-az, contact-za, date-newest, date-oldest
  
  // Lead Gen Categories - synced with Lead Generation page via localStorage
  // Default categories match the Lead Generation page structure
  const defaultLeadCategories = [
    { id: 'all', label: 'All Leads', color: 'bg-gray-500', isDefault: true, keywords: [] },
    { id: 'technology', label: 'Technology', color: 'bg-blue-500', isDefault: true, keywords: ['tech', 'software', 'saas', 'ai', 'machine learning', 'cloud', 'digital', 'app', 'platform', 'startup'] },
    { id: 'healthcare', label: 'Healthcare', color: 'bg-green-500', isDefault: true, keywords: ['health', 'medical', 'hospital', 'clinic', 'doctor', 'pharma', 'wellness', 'biotech', 'therapy'] },
    { id: 'finance', label: 'Finance', color: 'bg-yellow-500', isDefault: true, keywords: ['bank', 'financial', 'investment', 'insurance', 'capital', 'fund', 'fintech', 'crypto', 'trading'] },
    { id: 'real estate', label: 'Real Estate', color: 'bg-purple-500', isDefault: true, keywords: ['property', 'real estate', 'realty', 'housing', 'mortgage', 'commercial', 'residential', 'broker'] },
    { id: 'retail', label: 'Retail', color: 'bg-pink-500', isDefault: true, keywords: ['store', 'shop', 'ecommerce', 'retail', 'consumer', 'brand', 'fashion', 'product'] },
    { id: 'marketing', label: 'Marketing', color: 'bg-orange-500', isDefault: true, keywords: ['marketing', 'advertising', 'media', 'agency', 'creative', 'branding', 'pr', 'social media'] },
    { id: 'education', label: 'Education', color: 'bg-cyan-500', isDefault: true, keywords: ['school', 'university', 'college', 'learning', 'training', 'academy', 'tutor', 'course'] },
    { id: 'other', label: 'Other', color: 'bg-gray-600', isDefault: true, keywords: [] },
  ];
  
  const [leadCategories, setLeadCategories] = useState(() => {
    const saved = localStorage.getItem('leadGenCategories');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return defaultLeadCategories;
      }
    }
    return defaultLeadCategories;
  });
  
  // State for managing categories
  const [showManageLeadCategories, setShowManageLeadCategories] = useState(false);
  const [newLeadCategoryName, setNewLeadCategoryName] = useState('');
  const [newLeadCategoryColor, setNewLeadCategoryColor] = useState('bg-cyan-600');
  const [newLeadCategoryKeywords, setNewLeadCategoryKeywords] = useState('');
  const [editingLeadCategory, setEditingLeadCategory] = useState(null);
  
  // Sync categories to localStorage (shared with Lead Gen page)
  useEffect(() => {
    localStorage.setItem('leadGenCategories', JSON.stringify(leadCategories));
  }, [leadCategories]);
  
  // Listen for localStorage changes from Lead Gen page
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'leadGenCategories' && e.newValue) {
        try {
          setLeadCategories(JSON.parse(e.newValue));
        } catch (err) {
          console.error('Failed to parse categories:', err);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  // Add new category
  const handleAddLeadCategory = () => {
    if (!newLeadCategoryName.trim()) return;
    
    const newCategory = {
      id: newLeadCategoryName.toLowerCase().replace(/\s+/g, '-'),
      label: newLeadCategoryName.trim(),
      color: newLeadCategoryColor,
      keywords: newLeadCategoryKeywords.split(',').map(k => k.trim()).filter(k => k)
    };
    
    if (leadCategories.some(c => c.id === newCategory.id)) {
      return; // Already exists
    }
    
    setLeadCategories([...leadCategories, newCategory]);
    setNewLeadCategoryName('');
    setNewLeadCategoryKeywords('');
  };
  
  // Update category
  const handleUpdateLeadCategory = () => {
    if (!editingLeadCategory || !newLeadCategoryName.trim()) return;
    
    setLeadCategories(leadCategories.map(c => 
      c.id === editingLeadCategory.id 
        ? { 
            ...c, 
            label: newLeadCategoryName.trim(),
            color: newLeadCategoryColor,
            keywords: newLeadCategoryKeywords.split(',').map(k => k.trim()).filter(k => k)
          }
        : c
    ));
    setEditingLeadCategory(null);
    setNewLeadCategoryName('');
    setNewLeadCategoryKeywords('');
  };
  
  // Delete category
  const handleDeleteLeadCategory = (categoryId) => {
    const cat = leadCategories.find(c => c.id === categoryId);
    if (cat?.isDefault || categoryId === 'all') return; // Can't delete default
    setLeadCategories(leadCategories.filter(c => c.id !== categoryId));
    if (selectedLeadCategory === categoryId) {
      setSelectedLeadCategory('all');
    }
  };
  
  // Edit company modal state
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editCompanyForm, setEditCompanyForm] = useState({
    name: '',
    website: '',
    industry: '',
    location: '',
    notes: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_phone: '',
    contact_title: ''
  });
  const [savingCompany, setSavingCompany] = useState(false);
  
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [icloudContacts, setIcloudContacts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncStatus, setSyncStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest'); // 'a-z', 'z-a', 'newest' - default to newest
  const [selectedContact, setSelectedContact] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContact, setEditedContact] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [linkedinImportedData, setLinkedinImportedData] = useState(null);
  const [linkedinUrlInput, setLinkedinUrlInput] = useState(''); // LinkedIn URL input field
  const [isSearchingLinkedIn, setIsSearchingLinkedIn] = useState(false); // For auto-search
  const [callingContactId, setCallingContactId] = useState(null); // Track which contact is being called
  const [uploadingContactImage, setUploadingContactImage] = useState(false); // Track image upload state
  const contactImageInputRef = React.useRef(null); // Ref for hidden file input
  const [cardUploadTargetContact, setCardUploadTargetContact] = useState(null); // Contact being updated from card
  const [uploadingCardPhotoId, setUploadingCardPhotoId] = useState(null); // Contact ID being uploaded from card
  const cardImageInputRef = React.useRef(null); // Ref for card-level photo upload

  // ---- Bulk selection state -----------------------------------------------
  // Set of stable contact IDs (icloud_id or id). Toggled per-card by a
  // checkbox that only appears while `bulkSelectMode` is true.
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  // Modal: target category for "Move N contacts to..."
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  // Action-in-progress flag (shared by both bulk move + bulk delete)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  // Contacts view mode — 'grid' (cards) or 'list' (compact rows). Persisted in
  // localStorage so the user's preference survives reloads.
  const [contactsViewMode, setContactsViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    return localStorage.getItem('contacts_view_mode') === 'list' ? 'list' : 'grid';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('contacts_view_mode', contactsViewMode);
    }
  }, [contactsViewMode]);
  
  // Collapse state for accounts container - collapsed by default
  const [isAccountsCollapsed, setIsAccountsCollapsed] = useState(true);
  
  // Use the Nylas accounts hook for unified account management
  // Using hideAccount instead of disconnectAccount to keep tabs page-specific
  const { 
    accounts: nylasAccounts, 
    loading: loadingNylasAccounts, 
    hideAccount: hideNylasAccountTab,
    fetchAccounts: refetchNylasAccounts 
  } = useNylasAccounts(userId, 'contacts');
  
  // Nylas contacts state (fetched based on accounts)
  const [nylasContacts, setNylasContacts] = useState([]);
  const [activeNylasAccount, setActiveNylasAccount] = useState('all'); // 'all', 'icloud', or specific email
  const [loadingNylasContacts, setLoadingNylasContacts] = useState(false);
  
  // Delete contact account confirmation state
  const [showDeleteContactAccountConfirm, setShowDeleteContactAccountConfirm] = useState(false);
  const [contactAccountToDelete, setContactAccountToDelete] = useState(null);
  const [deletingContactAccount, setDeletingContactAccount] = useState(false);
  
  // Manage Accounts Modal state
  const [showManageAccountsModal, setShowManageAccountsModal] = useState(false);
  
  // iCloud contacts sync status (from existing icloud contacts in DB)
  const [icloudContactsCount, setIcloudContactsCount] = useState(0);
  const [icloudSynced, setIcloudSynced] = useState(false);
  
  // iCloud credentials modal state
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showDisconnectIcloudModal, setShowDisconnectIcloudModal] = useState(false);
  const [disconnectingIcloud, setDisconnectingIcloud] = useState(false);
  const [showGoogleContactsModal, setShowGoogleContactsModal] = useState(false);
  const [showOutlookContactsModal, setShowOutlookContactsModal] = useState(false);
  const [showAddContactAccountModal, setShowAddContactAccountModal] = useState(false);
  const [icloudEmail, setIcloudEmail] = useState('');
  const [icloudAppPassword, setIcloudAppPassword] = useState('');
  const [credentialError, setCredentialError] = useState('');
  
  // Contact Accounts Management State (similar to email accounts)
  // Use user-specific localStorage key
  const [contactAccounts, setContactAccounts] = useState([]);
  
  // Load contact accounts when userId is available
  useEffect(() => {
    if (userId) {
      try {
        const stored = localStorage.getItem(`contact_accounts_${userId}`);
        if (stored) {
          setContactAccounts(JSON.parse(stored));
        } else {
          setContactAccounts([]);
        }
      } catch {
        setContactAccounts([]);
      }
    } else {
      setContactAccounts([]);
    }
  }, [userId]);
  
  // Save contact accounts when they change
  useEffect(() => {
    if (userId && contactAccounts.length > 0) {
      localStorage.setItem(`contact_accounts_${userId}`, JSON.stringify(contactAccounts));
    }
  }, [contactAccounts, userId]);
  
  const [activeContactAccount, setActiveContactAccount] = useState('icloud_default');
  const [newContactAccountName, setNewContactAccountName] = useState('');
  const [newContactAccountEmail, setNewContactAccountEmail] = useState('');
  const [newContactAccountType, setNewContactAccountType] = useState('icloud');
  const [newContactAccountPassword, setNewContactAccountPassword] = useState('');
  const [addContactAccountError, setAddContactAccountError] = useState('');
  
  // Default contact categories definition
  // Each category has a `keywords` array used by the auto-categorizer and
  // by the "Re-scan all contacts" action. Keywords are matched against the
  // contact's name, organization, job_title, emails and notes (lowercase).
  const defaultContactCategories = [
    { id: 'all', name: 'All', icon: '📋', color: 'bg-gray-800', textColor: 'text-white', keywords: [] },
    { id: 'passwords', name: 'Passwords', icon: '🔐', color: 'bg-indigo-700', textColor: 'text-white', keywords: ['password', 'login', 'credential', 'username', 'auth', 'access', 'security', 'pin', 'passcode', '1password', 'lastpass', 'bitwarden', 'vault'] },
    { id: 'real-estate', name: 'Real Estate', icon: '🏢', color: 'bg-blue-600', textColor: 'text-white', keywords: ['realtor', 'real estate', 'realty', 'broker', 'property', 'listing', 'mortgage', 'escrow', 'title'] },
    { id: 'healthcare', name: 'Healthcare', icon: '⚕️', color: 'bg-red-600', textColor: 'text-white', keywords: ['health', 'medical', 'doctor', 'dr.', 'hospital', 'clinic', 'physician', 'nurse', 'dental', 'pharma', 'therapy'] },
    { id: 'technology', name: 'Technology', icon: '💻', color: 'bg-purple-600', textColor: 'text-white', keywords: ['tech', 'software', 'developer', 'engineer', 'computer', 'digital', 'cyber', 'saas', 'ai', 'cloud'] },
    { id: 'finance', name: 'Finance', icon: '💰', color: 'bg-green-600', textColor: 'text-white', keywords: ['financ', 'bank', 'invest', 'cpa', 'tax', 'capital', 'fund', 'wealth', 'crypto', 'fintech', 'trading'] },
    { id: 'sports', name: 'Sports', icon: '⚽', color: 'bg-orange-600', textColor: 'text-white', keywords: ['sport', 'athlete', 'coach', 'team', 'nfl', 'nba', 'football', 'basketball', 'baseball'] },
    { id: 'legal', name: 'Legal', icon: '⚖️', color: 'bg-yellow-600', textColor: 'text-black', keywords: ['law', 'attorney', 'legal', 'lawyer', 'counsel', 'esq'] },
    { id: 'education', name: 'Education', icon: '🎓', color: 'bg-indigo-600', textColor: 'text-white', keywords: ['school', 'university', 'college', 'education', 'teacher', 'professor', 'tutor', 'academy', 'training'] },
    { id: 'services', name: 'Services', icon: '🔧', color: 'bg-teal-600', textColor: 'text-white', keywords: ['service', 'consultant', 'advisor', 'consulting', 'agency'] },
    { id: 'automotive', name: 'Automotive', icon: '🚗', color: 'bg-red-700', textColor: 'text-white', keywords: ['auto', 'car', 'vehicle', 'dealership', 'automotive'] },
    { id: 'family', name: 'Family', icon: '👨‍👩‍👧‍👦', color: 'bg-pink-600', textColor: 'text-white', keywords: ['wife', 'husband', 'son', 'daughter', 'mom', 'dad', 'brother', 'sister', 'family', 'spouse'] },
    { id: 'friends', name: 'Friends', icon: '👥', color: 'bg-cyan-600', textColor: 'text-white', keywords: [] },
    { id: 'other', name: 'Other', icon: '📁', color: 'bg-gray-600', textColor: 'text-white', keywords: [] }
  ];
  
  // Use category tabs hook for contact categories (refactored)
  // Now syncs to database for persistent storage across deployments
  const {
    categories,
    setCategories: setCustomContactCategories,
    addCategory: addCategoryToHook,
    removeCategory: removeCategoryFromHook,
    reorderCategories,
    editingCategory: editingContactCategory,
    editValue: editContactCategoryValue,
    setEditValue: setEditContactCategoryValue,
    startEditing: startEditingContactCategory,
    saveEditing: saveEditingContactCategory,
    cancelEditing: cancelEditingContactCategory,
    isLoading: categoriesLoading,
    isSyncing: categoriesSyncing
  } = useCategoryTabs('contact_categories', defaultContactCategories, { userId, syncToDatabase: true });

  // Alphabetically-sorted view of the category list — "All" stays pinned at
  // the top, everything else is ordered by name (case-insensitive). Used in
  // the sidebar, mobile tab strip, and the various "pick a category" modals.
  // Note: the raw `categories` array is still used by code paths that depend
  // on auto-categorize priority (drag-to-reorder in the Edit Categories
  // modal continues to control matching priority).
  const sortedCategories = React.useMemo(() => {
    const list = Array.isArray(categories) ? [...categories] : [];
    const allCat = list.find(c => c.id === 'all');
    const rest = list.filter(c => c.id !== 'all')
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    return allCat ? [allCat, ...rest] : rest;
  }, [categories]);


  
  const [showContactCategoryModal, setShowContactCategoryModal] = useState(false);
  const [newContactCategoryName, setNewContactCategoryName] = useState('');
  const [newContactCategoryIcon, setNewContactCategoryIcon] = useState('📁');
  // Keyword editor: which category row is expanded + its working comma-separated value
  const [editingKeywordsFor, setEditingKeywordsFor] = useState(null);
  const [editingKeywordsValue, setEditingKeywordsValue] = useState('');
  // Re-scan progress for the "Re-scan All Contacts" button
  const [isRescanningContacts, setIsRescanningContacts] = useState(false);
  const [rescanProgress, setRescanProgress] = useState({ done: 0, total: 0, moved: 0 });
  // CSV export progress
  const [isExportingCsv, setIsExportingCsv] = useState(false);

  // Download all contacts (for current user) as a CSV file.
  // Uses fetch+blob so we can catch failures cleanly and date-stamp the filename.
  const exportContactsCsv = async () => {
    if (!userId) {
      toast.error('User not loaded yet — try again in a moment.');
      return;
    }
    setIsExportingCsv(true);
    try {
      const apiBase = window.location.origin;
      const res = await fetch(
        `${apiBase}/api/icloud-contacts/export-csv?user_id=${encodeURIComponent(userId)}`,
        { method: 'GET' }
      );
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) detail = j.detail;
        } catch { /* not json */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const today = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `operatr_contacts_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Contacts CSV downloaded.');
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error(`Couldn't export CSV — ${err.message || 'unknown error'}`);
    } finally {
      setIsExportingCsv(false);
    }
  };
  
  // State for editing category on individual contact cards
  const [editingContactCardCategory, setEditingContactCardCategory] = useState(null); // Contact ID being edited
  
  // Available emoji icons for categories
  const categoryIcons = ['📋', '🔐', '🏢', '⚕️', '💻', '💰', '⚽', '⚖️', '🎓', '🔧', '🚗', '👨‍👩‍👧‍👦', '👥', '📁', '🎯', '🌟', '💼', '🎨', '🎵', '✈️', '🍽️', '🛒', '📧', '📱', '🏠', '💡', '🔒', '📊', '🎮', '🏋️'];
  
  // Save custom categories to localStorage
  
  // Fetch lead contacts from lead generation API
  const fetchLeadContacts = async () => {
    setLoadingLeadContacts(true);
    try {
      const apiBase = window.location.origin;
      const response = await fetch(`${apiBase}/api/lead-gen/companies`);
      if (response.ok) {
        const data = await response.json();
        const companies = data.companies || [];
        
        // Store full company data for editing
        setLeadCompanies(companies);
        
        // Group contacts by company - each company becomes one card with all its contacts
        const companyCards = companies.map(company => {
          const allContacts = [];
          
          // Add primary contact
          if (company.primary_contact) {
            allContacts.push(company.primary_contact);
          }
          
          // Add additional contacts (avoiding duplicates)
          if (company.contacts && company.contacts.length > 0) {
            for (const contact of company.contacts) {
              if (!company.primary_contact || company.primary_contact.id !== contact.id) {
                allContacts.push(contact);
              }
            }
          }
          
          return {
            company_id: company.id,
            company_name: company.name || company.company_name,
            company_logo: company.logo_url,
            company_industry: company.industry,
            company_location: company.location,
            company_website: company.website,
            company_notes: company.notes,
            contacts: allContacts,
            source: 'lead_gen'
          };
        });
        
        setLeadContacts(companyCards);
      }
    } catch (error) {
      console.error('Failed to fetch lead contacts:', error);
    } finally {
      setLoadingLeadContacts(false);
    }
  };
  
  // Open edit company modal
  const handleEditLeadCompany = (companyCard) => {
    const company = leadCompanies.find(c => c.id === companyCard.company_id);
    if (company) {
      setEditingCompany(company);
      const primaryContact = company.primary_contact || company.contacts?.[0] || {};
      const nameParts = (primaryContact.name || '').split(' ');
      setEditCompanyForm({
        name: company.name || company.company_name || '',
        website: company.website || '',
        industry: company.industry || '',
        location: company.location || '',
        notes: company.notes || '',
        contact_first_name: primaryContact.first_name || nameParts[0] || '',
        contact_last_name: primaryContact.last_name || nameParts.slice(1).join(' ') || '',
        contact_email: primaryContact.email || '',
        contact_phone: primaryContact.phone || '',
        contact_title: primaryContact.title || ''
      });
      setShowEditCompanyModal(true);
    }
  };
  
  // Save company changes
  const handleSaveLeadCompany = async () => {
    if (!editingCompany) return;
    
    setSavingCompany(true);
    try {
      const apiBase = window.location.origin;
      
      // Update company
      const companyResponse = await fetch(`${apiBase}/api/lead-gen/companies/${editingCompany.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editCompanyForm.name,
          website: editCompanyForm.website,
          industry: editCompanyForm.industry,
          location: editCompanyForm.location,
          notes: editCompanyForm.notes
        })
      });
      
      // Update or create contact
      const contactName = `${editCompanyForm.contact_first_name} ${editCompanyForm.contact_last_name}`.trim();
      if (contactName || editCompanyForm.contact_email) {
        const existingContact = editingCompany.primary_contact || editingCompany.contacts?.[0];
        
        if (existingContact?.id) {
          // Update existing contact
          await fetch(`${apiBase}/api/lead-gen/contacts/${existingContact.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: contactName,
              first_name: editCompanyForm.contact_first_name,
              last_name: editCompanyForm.contact_last_name,
              email: editCompanyForm.contact_email,
              phone: editCompanyForm.contact_phone,
              title: editCompanyForm.contact_title
            })
          });
        } else {
          // Create new contact
          await fetch(`${apiBase}/api/lead-gen/contacts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              company_id: editingCompany.id,
              name: contactName,
              first_name: editCompanyForm.contact_first_name,
              last_name: editCompanyForm.contact_last_name,
              email: editCompanyForm.contact_email,
              phone: editCompanyForm.contact_phone,
              title: editCompanyForm.contact_title
            })
          });
        }
      }
      
      // Refresh data
      await fetchLeadContacts();
      setShowEditCompanyModal(false);
      setEditingCompany(null);
    } catch (error) {
      console.error('Failed to save company:', error);
    } finally {
      setSavingCompany(false);
    }
  };
  
  // Fetch lead contacts when tab changes to leads
  useEffect(() => {
    if (mainContactTab === 'leads') {
      fetchLeadContacts();
    }
  }, [mainContactTab]);
  
  // Save contact accounts to localStorage
  // Save contact accounts to user-specific localStorage when they change
  useEffect(() => {
    if (userId) {
      localStorage.setItem(`contact_accounts_${userId}`, JSON.stringify(contactAccounts));
    }
  }, [contactAccounts, userId]);
  
  // Keep 'all' as the default tab - don't auto-switch to first account
  // Users can manually select a specific account tab if needed
  
  // Fetch Nylas contacts when active account changes
  useEffect(() => {
    const fetchNylasContacts = async () => {
      if (nylasAccounts.length === 0) return;
      
      setLoadingNylasContacts(true);
      try {
        // Use window.location.origin for same-origin requests
        const apiBase = window.location.origin;
        
        // Fetch contacts from all accounts or a specific one
        const accountsToFetch = activeNylasAccount === 'all' 
          ? nylasAccounts 
          : nylasAccounts.filter(a => a.email === activeNylasAccount);
        
        const allContacts = [];
        
        for (const account of accountsToFetch) {
          try {
            // Use email parameter to look up grant_id on the backend
            const response = await fetch(
              `${apiBase}/api/nylas/contacts?user_id=${userId}&email=${encodeURIComponent(account.email)}&limit=200`
            );
            if (response.ok) {
              const data = await response.json();
              if (data.contacts) {
                // Add account email to each contact for filtering
                const contactsWithAccount = data.contacts.map(c => ({
                  ...c,
                  account_email: account.email,
                  source: 'nylas'
                }));
                allContacts.push(...contactsWithAccount);
              }
            }
          } catch (err) {
            console.error(`Error fetching contacts for ${account.email}:`, err);
          }
        }
        
        setNylasContacts(allContacts);
      } catch (error) {
        console.error('Error fetching Nylas contacts:', error);
      } finally {
        setLoadingNylasContacts(false);
      }
    };
    
    fetchNylasContacts();
  }, [nylasAccounts, activeNylasAccount]);
  
  // Handle adding new contact account
  const handleAddContactAccount = () => {
    if (!newContactAccountName.trim()) {
      setAddContactAccountError('Please enter a name for this contact account');
      return;
    }
    if (newContactAccountType === 'icloud' && !newContactAccountPassword.trim()) {
      setAddContactAccountError('Please enter your app-specific password for iCloud');
      return;
    }
    
    const newAccount = {
      id: `${newContactAccountType}_${Date.now()}`,
      name: newContactAccountName.trim(),
      type: newContactAccountType,
      email: newContactAccountEmail.trim(),
      password: newContactAccountPassword.trim() || null,
      connected: newContactAccountType === 'icloud' && newContactAccountPassword.trim() ? true : false,
      contactCount: 0
    };
    
    // Store credentials for the account
    if (newContactAccountPassword.trim()) {
      localStorage.setItem(`contact_creds_${newAccount.id}`, JSON.stringify({
        email: newContactAccountEmail.trim(),
        password: newContactAccountPassword.trim()
      }));
    }
    
    setContactAccounts(prev => [...prev, newAccount]);
    setActiveContactAccount(newAccount.id);
    setShowAddContactAccountModal(false);
    setNewContactAccountName('');
    setNewContactAccountEmail('');
    setNewContactAccountType('icloud');
    setNewContactAccountPassword('');
    setAddContactAccountError('');
  };
  
  // Handle disconnecting a contact account
  const handleDisconnectContactAccount = (accountId) => {
    setContactAccounts(prev => prev.map(acc => 
      acc.id === accountId ? { ...acc, connected: false } : acc
    ));
    localStorage.removeItem(`contact_creds_${accountId}`);
  };
  
  // Handle removing a contact account
  const handleRemoveContactAccount = (accountId) => {
    setContactAccounts(prev => prev.filter(acc => acc.id !== accountId));
    localStorage.removeItem(`contact_creds_${accountId}`);
    if (activeContactAccount === accountId) {
      setActiveContactAccount(contactAccounts[0]?.id || 'icloud_default');
    }
  };
  
  const contactsPerPage = 50;
  
  // Get API URL - use same origin to avoid CORS issues
  const getApiUrl = () => {
    if (typeof window !== 'undefined') {
      // Always use same origin - works for localhost, preview, and production
      return window.location.origin;
    }
    return '';
  };
  
  const apiUrl = getApiUrl();
  
  // Handle deleting/disconnecting a contact account
  const handleDeleteContactAccount = (account) => {
    setContactAccountToDelete(account);
    setShowDeleteContactAccountConfirm(true);
  };
  
  // Confirm and execute contact account tab removal (page-specific, doesn't affect other pages)
  const confirmDeleteContactAccount = async () => {
    if (!contactAccountToDelete) return;
    
    try {
      setDeletingContactAccount(true);
      
      // Use hideAccount to only hide on THIS page (Contacts)
      // This does NOT disconnect from Nylas API, so Email and Calendar pages keep the account
      const identifier = contactAccountToDelete.grant_id || contactAccountToDelete.email;
      hideNylasAccountTab(identifier);
      
      // Remove contacts for this account from local state
      setNylasContacts(prev => prev.filter(c => c.account_email !== contactAccountToDelete.email));
      
      // Switch to 'all' tab if this was the active tab
      if (activeNylasAccount === contactAccountToDelete.email) {
        setActiveNylasAccount('all');
      }
      
      toast.success(`Removed ${contactAccountToDelete.account_name || contactAccountToDelete.email} from Contacts`);
    } catch (error) {
      console.error('Error removing contact account tab:', error);
      toast.error('Failed to remove account tab');
    } finally {
      setDeletingContactAccount(false);
      setShowDeleteContactAccountConfirm(false);
      setContactAccountToDelete(null);
    }
  };
  
  // Connect new contact account via Nylas
  const handleConnectContactAccount = () => {
    const accountName = prompt('Enter a name for this contact account (e.g., "Work Contacts", "Personal"):');
    if (accountName) {
      window.location.href = `${apiUrl}/api/nylas/auth/connect?user_id=${userId}&account_name=${encodeURIComponent(accountName)}&redirect_to=contacts&redirect_host=${encodeURIComponent(window.location.origin)}`;
    }
  };
  
  // Function to initiate a Twilio call
  const initiateCall = async (contact, phoneNumber) => {
    if (!phoneNumber) {
      alert('No phone number available for this contact');
      return;
    }
    
    try {
      setCallingContactId(contact.icloud_id || contact.id);
      
      const response = await apiCall('/calls/make-call', {
        method: 'POST',
        body: JSON.stringify({
          to_phone: phoneNumber,
          contact_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown',
          contact_id: contact.icloud_id || contact.id
        })
      });
      
      if (response.call_sid) {
        alert(`Call initiated to ${contact.first_name || phoneNumber}. Check the Communications page for call status.`);
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      alert('Failed to initiate call. Please try again.');
    } finally {
      setCallingContactId(null);
    }
  };
  
  // Function to open email client
  const sendEmail = (email) => {
    if (!email) {
      alert('No email address available for this contact');
      return;
    }
    window.location.href = `mailto:${email}`;
  };
  
  // Function to open edit modal for a contact
  const openEditModal = (contact, e) => {
    e.stopPropagation();
    setSelectedContact(contact);
    setEditedContact({
      ...contact,
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      organization: contact.organization || '',
      job_title: contact.job_title || '',
      emails: contact.emails || [],
      phones: contact.phones || [],
      notes: contact.notes || ''
    });
    setIsEditMode(true);
    setIsDetailModalOpen(true);
  };
  
  const openModal = (modalType) => {
    console.log('Opening modal:', modalType);
  };

  // Load cached contacts from localStorage and database on mount (NO AUTO-SYNC)
  useEffect(() => {
    const loadContacts = async () => {
      // Use demo data if in demo mode
      if (isDemoMode && demoData?.contacts) {
        const demoContacts = demoData.contacts.map(c => ({
          id: c.id,
          first_name: c.name.split(' ')[0] || c.name,
          last_name: c.name.split(' ').slice(1).join(' ') || '',
          emails: c.email ? [{ email: c.email }] : [],
          phones: c.phone ? [{ phone: c.phone }] : [],
          organization: c.company || '',
          job_title: c.category || '',
          notes: c.notes || ''
        }));
        setIcloudContacts(demoContacts);
        setIcloudContactsCount(demoContacts.length);
        setIcloudSynced(true);
        setIsAuthenticated(true);
        console.log(`📇 Demo mode: Loaded ${demoContacts.length} demo contacts`);
        return;
      }
      
      console.log('🔄 Loading contacts...');
      
      // First try localStorage for instant loading
      const cachedContacts = localStorage.getItem(`icloud_contacts_${userId}`);
      const cachedLastSync = localStorage.getItem('icloud_last_sync');
      const cachedAuthStatus = localStorage.getItem('icloud_authenticated');
      
      if (cachedContacts) {
        try {
          const contacts = JSON.parse(cachedContacts);
          setIcloudContacts(contacts);
          setIcloudContactsCount(contacts.length);
          setIcloudSynced(contacts.length > 0);
          console.log(`✅ Loaded ${contacts.length} cached contacts from localStorage`);
        } catch (e) {
          console.error('❌ Failed to parse cached contacts:', e);
          localStorage.removeItem(`icloud_contacts_${userId}`);
        }
      }
      
      if (cachedLastSync) {
        try {
          setLastSync(new Date(cachedLastSync));
        } catch (e) {
          console.error('Failed to parse last sync date');
        }
      }
      
      if (cachedAuthStatus === 'true') {
        setIsAuthenticated(true);
      }
      
      // Then try to load from database (more reliable than localStorage)
      try {
        const xhr = new XMLHttpRequest();
        xhr.timeout = 15000;
        xhr.open('GET', `${apiUrl}/api/icloud-contacts/stored?user_id=${userId}`, true);
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              if (data.contacts && data.contacts.length > 0) {
                setIcloudContacts(data.contacts);
                setIcloudContactsCount(data.contacts.length);
                setIcloudSynced(true);
                localStorage.setItem(`icloud_contacts_${userId}`, JSON.stringify(data.contacts));
                console.log(`✅ Loaded ${data.contacts.length} contacts from database`);
              }
            } catch (e) {
              console.error('Failed to parse database contacts');
            }
          }
        };
        xhr.onerror = () => console.log('Could not load contacts from database');
        xhr.ontimeout = () => console.log('Database contacts request timed out');
        xhr.send();
      } catch (e) {
        console.log('Error loading from database:', e);
      }
    };
    
    loadContacts();
  }, [apiUrl, userId, isDemoMode, demoData]);

  const authenticateAndSync = async (appleId = null, appPassword = null) => {
    // Get credentials from parameters, state, or localStorage
    const email = appleId || icloudEmail || localStorage.getItem('icloud_email');
    const password = appPassword || icloudAppPassword || localStorage.getItem('icloud_app_password');
    
    if (!email || !password) {
      setShowCredentialsModal(true);
      return;
    }
    
    setAuthenticating(true);
    setSyncStatus('🔄 Connecting to iCloud...');
    setCredentialError('');
    
    try {
      // Step 1: Authenticate - use XMLHttpRequest with timeout
      console.log('🔄 Authenticating with iCloud...');
      console.log('API URL:', apiUrl);
      
      const authData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.timeout = 30000; // 30 second timeout for auth
        xhr.open('POST', `${apiUrl}/api/icloud-contacts/authenticate?apple_id=${encodeURIComponent(email)}&app_password=${encodeURIComponent(password)}&user_id=${userId}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid response'));
            }
          } else if (xhr.status === 502 || xhr.status === 504) {
            reject(new Error('Server timeout. Please try again.'));
          } else if (xhr.status === 401) {
            reject(new Error('Invalid credentials. Please check your Apple ID and App Password.'));
          } else {
            reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.ontimeout = () => reject(new Error('Request timed out. Please try again.'));
        xhr.send();
      });
      
      console.log('✅ Authenticated:', authData.message);
      setIsAuthenticated(true);
      localStorage.setItem('icloud_authenticated', 'true');
      localStorage.setItem('icloud_email', email);
      localStorage.setItem('icloud_app_password', password);
      setShowCredentialsModal(false);
      
      // Step 2: Sync contacts - longer timeout for sync operation
      setSyncStatus('🔄 Syncing contacts (this may take a minute)...');
      
      const syncData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.timeout = 120000; // 2 minute timeout for sync (iCloud can be slow)
        xhr.open('POST', `${apiUrl}/api/icloud-contacts/quick-sync?user_id=${userId}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid response'));
            }
          } else if (xhr.status === 502 || xhr.status === 504) {
            reject(new Error('Server timeout during sync. Your contacts may still be syncing - please wait and refresh the page.'));
          } else {
            reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.ontimeout = () => reject(new Error('Sync timed out. Your contacts may still be syncing in the background.'));
        xhr.send();
      });
      
      console.log(`✅ Synced ${syncData.synced_count} contacts`);
      
      // Save to state and localStorage
      setIcloudContacts(syncData.contacts || []);
      const syncTime = new Date(syncData.last_sync);
      setLastSync(syncTime);
      
      localStorage.setItem(`icloud_contacts_${userId}`, JSON.stringify(syncData.contacts || []));
      localStorage.setItem('icloud_last_sync', syncTime.toISOString());
      
      setSyncStatus(`✅ ${syncData.synced_count} contacts synced`);
      
      setTimeout(() => setSyncStatus(''), 5000);
    } catch (error) {
      console.error('❌ Error:', error);
      const errorMsg = error.message || 'Unknown error';
      setSyncStatus(`❌ Sync failed: ${errorMsg}`);
      
      // If credential error, show modal again
      if (errorMsg.includes('Invalid credentials') || errorMsg.includes('401')) {
        setCredentialError('Invalid credentials. Please check your Apple ID and App Password.');
        setShowCredentialsModal(true);
        localStorage.removeItem('icloud_email');
        localStorage.removeItem('icloud_app_password');
        localStorage.removeItem('icloud_authenticated');
        setIsAuthenticated(false);
      }
      
      setTimeout(() => setSyncStatus(''), 5000);
    } finally {
      setAuthenticating(false);
      setSyncing(false);
    }
  };

  // Manual sync - ONLY called when button is clicked
  const handleSync = async () => {
    if (!isAuthenticated) {
      // Check if we have stored credentials
      const storedEmail = localStorage.getItem('icloud_email');
      const storedPassword = localStorage.getItem('icloud_app_password');
      
      if (storedEmail && storedPassword) {
        await authenticateAndSync(storedEmail, storedPassword);
      } else {
        setShowCredentialsModal(true);
      }
      return;
    }
    
    setSyncing(true);
    setSyncStatus('🔄 Syncing...');
    
    try {
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/api/icloud-contacts/quick-sync?user_id=${userId}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid response'));
            }
          } else {
            reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      
      // Update state and cache
      setIcloudContacts(data.contacts || []);
      const syncTime = new Date(data.last_sync);
      setLastSync(syncTime);
      
      localStorage.setItem(`icloud_contacts_${userId}`, JSON.stringify(data.contacts || []));
      localStorage.setItem('icloud_last_sync', syncTime.toISOString());
      
      setSyncStatus(`✅ ${data.synced_count} contacts synced`);
      
      setTimeout(() => setSyncStatus(''), 3000);
    } catch (error) {
      console.error('Sync error:', error);
      setSyncStatus(`❌ Sync failed: ${error.message}`);
      setTimeout(() => setSyncStatus(''), 3000);
    } finally {
      setSyncing(false);
    }
  };

  // Disconnect iCloud contacts
  const handleDisconnectIcloud = async () => {
    setDisconnectingIcloud(true);
    
    try {
      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/api/icloud-contacts/disconnect?user_id=${userId}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Failed to parse response'));
            }
          } else {
            reject(new Error(`Failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      
      if (response.success) {
        // Clear local state
        setIcloudContacts([]);
        setIcloudContactsCount(0);
        setIcloudSynced(false);
        setIsAuthenticated(false);
        
        // Clear localStorage
        localStorage.removeItem(`icloud_contacts_${userId}`);
        localStorage.removeItem('icloud_email');
        localStorage.removeItem('icloud_app_password');
        localStorage.removeItem('icloud_authenticated');
        localStorage.removeItem('icloud_last_sync');
        
        // Switch to 'all' tab
        setActiveNylasAccount('all');
        
        toast.success(`iCloud disconnected. ${response.deleted_count} contacts removed.`);
      } else {
        toast.error('Failed to disconnect iCloud');
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error(`Failed to disconnect: ${error.message}`);
    } finally {
      setDisconnectingIcloud(false);
      setShowDisconnectIcloudModal(false);
    }
  };

  // Category Management Functions (refactored to use useCategoryTabs hook)
  const generateCategoryColor = (index) => {
    const colors = [
      { color: 'bg-blue-600', textColor: 'text-white' },
      { color: 'bg-purple-600', textColor: 'text-white' },
      { color: 'bg-pink-600', textColor: 'text-white' },
      { color: 'bg-red-600', textColor: 'text-white' },
      { color: 'bg-orange-600', textColor: 'text-white' },
      { color: 'bg-green-600', textColor: 'text-white' },
      { color: 'bg-teal-600', textColor: 'text-white' },
      { color: 'bg-cyan-600', textColor: 'text-white' },
      { color: 'bg-indigo-600', textColor: 'text-white' }
    ];
    return colors[index % colors.length];
  };
  
  const addContactCategory = () => {
    if (!newContactCategoryName.trim()) return;
    const categoryId = newContactCategoryName.toLowerCase().replace(/\s+/g, '-');
    
    // Check if already exists
    if (categories.some(c => c.id === categoryId)) {
      toast.error('Category already exists');
      return;
    }
    
    const colorConfig = generateCategoryColor(categories.length);
    const newCategory = {
      id: categoryId,
      name: newContactCategoryName,
      icon: newContactCategoryIcon,
      keywords: [],
      ...colorConfig
    };
    
    // Use hook's addCategory which handles localStorage
    const success = addCategoryToHook(newCategory);
    if (success) {
      setNewContactCategoryName('');
      setNewContactCategoryIcon('📁');
      toast.success(`Added "${newContactCategoryName}" category`);
    }
  };
  
  const updateContactCategory = (oldId, newName, newIcon) => {
    if (!newName.trim()) return;
    const newId = newName.toLowerCase().replace(/\s+/g, '-');
    
    const updatedCategories = categories.map(c => {
      if (c.id === oldId) {
        return { ...c, id: newId, name: newName, icon: newIcon || c.icon };
      }
      return c;
    });
    
    // Use hook's setCategories which handles localStorage
    setCustomContactCategories(updatedCategories);
    if (selectedCategory === oldId) setSelectedCategory(newId);
    cancelEditingContactCategory();
    toast.success(`Category renamed to "${newName}"`);
  };
  
  const deleteContactCategory = (categoryId) => {
    // Protected categories
    const protectedCategories = ['all', 'passwords', 'real-estate', 'family', 'other'];
    if (protectedCategories.includes(categoryId)) {
      toast.error('Cannot delete default category');
      return;
    }
    
    // Use hook's removeCategory which handles localStorage
    removeCategoryFromHook(categoryId);
    if (selectedCategory === categoryId) setSelectedCategory('all');
    toast.success('Category deleted');
  };

  // Open the keyword editor for a single category. Prefills the input with
  // the current keywords joined by commas.
  const openKeywordEditor = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    setEditingKeywordsValue(Array.isArray(cat?.keywords) ? cat.keywords.join(', ') : '');
    setEditingKeywordsFor(categoryId);
  };

  // Persist edited keywords to the category list via the useCategoryTabs hook,
  // which also syncs the change to the backend (per-user settings).
  const saveCategoryKeywords = (categoryId) => {
    const keywords = editingKeywordsValue
      .split(',')
      .map(k => k.trim().toLowerCase())
      .filter(k => k.length > 0);
    const updated = categories.map(c => c.id === categoryId ? { ...c, keywords } : c);
    setCustomContactCategories(updated);
    setEditingKeywordsFor(null);
    setEditingKeywordsValue('');
    toast.success(`Keywords saved for "${categories.find(c => c.id === categoryId)?.name || categoryId}"`);
  };

  // Re-scan every loaded contact using the current per-category keywords and
  // move any contact whose computed category differs from its stored one.
  // Manual overrides (localStorage) are always respected by categorizeContact.
  const rescanAllContacts = async () => {
    if (isRescanningContacts) return;
    const allContacts = [
      ...(icloudContacts || []),
      ...(nylasContacts || []),
    ];
    if (allContacts.length === 0) {
      toast.error('No contacts loaded yet — try again after they sync.');
      return;
    }

    setIsRescanningContacts(true);
    setRescanProgress({ done: 0, total: allContacts.length, moved: 0 });

    let moved = 0;
    let done = 0;

    // Direct PATCH helper — avoids the per-contact toast spam that
    // updateSingleContactCategory triggers when editing one card.
    const patchCategory = async (contact, newCategoryId) => {
      const isNylas = contact.source === 'nylas';
      const body = {
        contact_id: contact.icloud_id || contact.id,
        user_id: userId,
        category: newCategoryId,
        first_name: contact.first_name || contact.given_name || contact.name?.split(' ')[0] || '',
        last_name: contact.last_name || contact.surname || contact.name?.split(' ').slice(1).join(' ') || '',
        phones: contact.phones || (contact.phone_numbers?.map(p => ({ number: p.number, type: p.type })) ?? []),
        emails: contact.emails?.map(e => e.email ? { value: e.email, type: e.type } : e) || [],
        organization: contact.organization || contact.company_name || '',
        job_title: contact.job_title || '',
      };
      const res = await fetch(`${apiUrl}/api/icloud-contacts/update-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      // Update local state in place
      if (isNylas) {
        setNylasContacts(prev => prev.map(c => c.id === contact.id ? { ...c, category: newCategoryId } : c));
      } else if (contact.icloud_id) {
        setIcloudContacts(prev => prev.map(c => c.icloud_id === contact.icloud_id ? { ...c, category: newCategoryId } : c));
      }
    };

    for (const contact of allContacts) {
      const newCat = categorizeContact(contact, { force: true });
      const currentCat = contact.category || 'other';
      if (newCat && newCat !== currentCat) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await patchCategory(contact, newCat);
          moved += 1;
        } catch (err) {
          console.error('rescanAllContacts: failed for', contact?.icloud_id || contact?.id, err);
        }
      }
      done += 1;
      setRescanProgress({ done, total: allContacts.length, moved });
    }

    setIsRescanningContacts(false);
    toast.success(`Re-scan complete — ${moved} contact${moved === 1 ? '' : 's'} moved to a new category.`);
  };

  // ---------------------------------------------------------------------------
  // Bulk selection helpers
  // ---------------------------------------------------------------------------

  // Stable selection key — prefers iCloud's stable id, falls back to the
  // generic id. We never mix shapes within the same call.
  const getContactKey = (contact) => contact?.icloud_id || contact?.id || '';

  // Toggle one contact in/out of the selection set
  const toggleContactSelection = (contact) => {
    const key = getContactKey(contact);
    if (!key) return;
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Select/deselect every contact currently visible on this page
  const toggleSelectAllVisible = (visibleContacts) => {
    const visibleKeys = visibleContacts.map(getContactKey).filter(Boolean);
    setSelectedContactIds(prev => {
      const allSelected = visibleKeys.every(k => prev.has(k));
      const next = new Set(prev);
      if (allSelected) visibleKeys.forEach(k => next.delete(k));
      else visibleKeys.forEach(k => next.add(k));
      return next;
    });
  };

  const exitBulkMode = () => {
    setBulkSelectMode(false);
    setSelectedContactIds(new Set());
    setShowBulkCategoryModal(false);
  };

  // Bulk-move selected contacts to `newCategoryId`. Uses the same backend
  // endpoint as the single-contact edit. Errors per-contact are logged but
  // do not abort the batch.
  const bulkChangeCategory = async (newCategoryId) => {
    if (!newCategoryId) return;
    const allContacts = [...(icloudContacts || []), ...(nylasContacts || [])];
    const targets = allContacts.filter(c => selectedContactIds.has(getContactKey(c)));
    if (targets.length === 0) return;

    setIsBulkProcessing(true);
    setBulkProgress({ done: 0, total: targets.length });
    let done = 0;
    let success = 0;

    for (const contact of targets) {
      try {
        const body = {
          contact_id: contact.icloud_id || contact.id,
          user_id: userId,
          category: newCategoryId,
          first_name: contact.first_name || contact.given_name || contact.name?.split(' ')[0] || '',
          last_name: contact.last_name || contact.surname || contact.name?.split(' ').slice(1).join(' ') || '',
          phones: contact.phones || (contact.phone_numbers?.map(p => ({ number: p.number, type: p.type })) ?? []),
          emails: contact.emails?.map(e => e.email ? { value: e.email, type: e.type } : e) || [],
          organization: contact.organization || contact.company_name || '',
          job_title: contact.job_title || '',
        };
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(`${apiUrl}/api/icloud-contacts/update-contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          if (contact.source === 'nylas') {
            setNylasContacts(prev => prev.map(c => c.id === contact.id ? { ...c, category: newCategoryId } : c));
          } else if (contact.icloud_id) {
            setIcloudContacts(prev => prev.map(c => c.icloud_id === contact.icloud_id ? { ...c, category: newCategoryId } : c));
          }
          success += 1;
        }
      } catch (err) {
        console.error('bulkChangeCategory failed for', getContactKey(contact), err);
      }
      done += 1;
      setBulkProgress({ done, total: targets.length });
    }

    setIsBulkProcessing(false);
    setShowBulkCategoryModal(false);
    const catName = categories.find(c => c.id === newCategoryId)?.name || newCategoryId;
    toast.success(`Moved ${success} of ${targets.length} contact${targets.length === 1 ? '' : 's'} to "${catName}".`);
    exitBulkMode();
  };

  // Bulk delete — same iCloud DELETE endpoint as the single-contact path.
  // Nylas contacts can't be deleted from here today, so they're skipped with
  // a console warning (matching the existing single-contact behavior).
  const bulkDeleteSelected = async () => {
    const allContacts = [...(icloudContacts || []), ...(nylasContacts || [])];
    const targets = allContacts.filter(c => selectedContactIds.has(getContactKey(c)));
    if (targets.length === 0) return;
    if (!window.confirm(`Delete ${targets.length} contact${targets.length === 1 ? '' : 's'}? This cannot be undone.`)) return;

    setIsBulkProcessing(true);
    setBulkProgress({ done: 0, total: targets.length });
    let done = 0;
    let success = 0;

    for (const contact of targets) {
      const cid = contact.icloud_id || contact.id;
      if (!cid || contact.source === 'nylas') {
        // Nylas contact deletion isn't wired to this DELETE endpoint
        if (contact.source === 'nylas') console.warn('Skipping Nylas bulk delete:', cid);
        done += 1;
        setBulkProgress({ done, total: targets.length });
        continue;
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(
          `${apiUrl}/api/icloud-contacts/delete/${encodeURIComponent(cid)}?user_id=${encodeURIComponent(userId)}`,
          { method: 'DELETE' }
        );
        if (res.ok) {
          setIcloudContacts(prev => prev.filter(c => c.icloud_id !== contact.icloud_id && c.id !== contact.id));
          success += 1;
        }
      } catch (err) {
        console.error('bulkDeleteSelected failed for', cid, err);
      }
      done += 1;
      setBulkProgress({ done, total: targets.length });
    }

    setIsBulkProcessing(false);
    toast.success(`Deleted ${success} of ${targets.length} contact${targets.length === 1 ? '' : 's'}.`);
    exitBulkMode();
  };


  // Update a single contact's category assignment
  const updateSingleContactCategory = async (contact, newCategoryId) => {
    const contactId = contact.id || contact.icloud_id;
    
    // For iCloud contacts, update them in the backend
    if (contact.icloud_id) {
      try {
        // Use POST with contact_id in body to handle IDs with special characters (slashes)
        const response = await fetch(`${apiUrl}/api/icloud-contacts/update-contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: contact.icloud_id,
            user_id: userId,
            category: newCategoryId,
            first_name: contact.first_name || contact.name?.split(' ')[0] || '',
            last_name: contact.last_name || contact.name?.split(' ').slice(1).join(' ') || '',
            phones: contact.phones || [],
            emails: contact.emails || [],
            organization: contact.organization || '',
            job_title: contact.job_title || ''
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to update: ${response.status}`);
        }
        
        // Update local state
        setIcloudContacts(prev => prev.map(c => 
          c.icloud_id === contact.icloud_id ? { ...c, category: newCategoryId } : c
        ));
        
        toast.success('Category updated');
      } catch (error) {
        console.error('Error updating contact category:', error);
        toast.error('Failed to update category');
      }
    } else if (contact.source === 'nylas') {
      // For Nylas contacts, also call the backend
      try {
        // Use POST with contact_id in body to handle IDs with special characters
        const response = await fetch(`${apiUrl}/api/icloud-contacts/update-contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: contact.id,
            user_id: userId,
            category: newCategoryId,
            first_name: contact.given_name || contact.name?.split(' ')[0] || '',
            last_name: contact.surname || contact.name?.split(' ').slice(1).join(' ') || '',
            phones: contact.phone_numbers?.map(p => ({ number: p.number, type: p.type })) || [],
            emails: contact.emails?.map(e => ({ value: e.email, type: e.type })) || [],
            organization: contact.company_name || '',
            job_title: contact.job_title || ''
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to update: ${response.status}`);
        }
        
        setNylasContacts(prev => prev.map(c => 
          c.id === contact.id ? { ...c, category: newCategoryId } : c
        ));
        
        toast.success('Category updated');
      } catch (error) {
        console.error('Error updating contact category:', error);
        toast.error('Failed to update category');
      }
    } else {
      // For other contacts (manually added), also call the backend
      try {
        // Use POST with contact_id in body to handle IDs with special characters
        const response = await fetch(`${apiUrl}/api/icloud-contacts/update-contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: contactId,
            user_id: userId,
            category: newCategoryId,
            first_name: contact.first_name || contact.name?.split(' ')[0] || '',
            last_name: contact.last_name || contact.name?.split(' ').slice(1).join(' ') || '',
            phones: contact.phones || [],
            emails: contact.emails || [],
            organization: contact.organization || '',
            job_title: contact.job_title || ''
          })
        });
        
        if (response.ok) {
          toast.success('Category updated');
        }
      } catch (error) {
        console.error('Error updating contact category:', error);
      }
    }
    
    setEditingContactCardCategory(null);
  };

  // Auto-categorize contacts based on their organization, emails, or notes.
  // Pass { force: true } from the "Re-scan all contacts" action so a stale
  // server-side `category` value gets re-evaluated. Manual user overrides
  // (saved in localStorage) are always respected.
  const categorizeContact = (contact, { force = false } = {}) => {
    // Manual override on the contact object itself — always respected
    if (contact.categoryOverride) {
      return contact.categoryOverride;
    }

    // Manual overrides stored in localStorage — always respected
    if (contact.icloud_id) {
      const overrides = JSON.parse(localStorage.getItem('contact_category_overrides') || '{}');
      if (overrides[contact.icloud_id]) {
        return overrides[contact.icloud_id];
      }
    }
    if (contact.id && contact.source === 'nylas') {
      const overrides = JSON.parse(localStorage.getItem('nylas_contact_category_overrides') || '{}');
      if (overrides[contact.id]) {
        return overrides[contact.id];
      }
    }

    // Respect any existing category UNLESS force-rescanning
    if (!force && contact.category && contact.category !== 'other') {
      return contact.category;
    }
    
    const searchText = `${contact.organization || ''} ${contact.emails?.map(e => e.email).join(' ') || ''} ${contact.notes || ''} ${contact.name || ''} ${contact.first_name || ''} ${contact.last_name || ''} ${contact.job_title || ''}`.toLowerCase();
    
    // Dynamic keyword-based matching — walks the user's editable category list
    // (categories from useCategoryTabs) and returns the first match in tab
    // order. Priority therefore mirrors the order shown to the user, which they
    // can drag to reorder in the Edit Categories modal.
    for (const cat of categories) {
      if (!cat || cat.id === 'all' || !Array.isArray(cat.keywords) || cat.keywords.length === 0) continue;
      const hit = cat.keywords.some(kw => {
        const k = (kw || '').toString().trim().toLowerCase();
        return k && searchText.includes(k);
      });
      if (hit) return cat.id;
    }

    // Friends fallback — personal contacts without organization
    if (!contact.organization || contact.organization.trim() === '') {
      return 'friends';
    }

    return 'other';
  };

  // No mock contacts - only iCloud contacts
  const contacts = [];

  // Remove duplicates based on email, phone, or exact name match
  const removeDuplicates = (contacts) => {
    const seen = new Map();
    const unique = [];
    
    contacts.forEach(contact => {
      // Create unique keys for duplicate detection — use name field as fallback
      const name = (contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`).trim().toLowerCase();
      const email = contact.emails?.[0]?.email?.toLowerCase() || '';
      const phone = contact.phones?.[0]?.number?.replace(/\D/g, '') || ''; // Remove non-digits
      
      // Create a composite key
      const key = `${name}|${email}|${phone}`;
      
      // Check if we've seen this contact before
      if (!seen.has(key)) {
        seen.set(key, true);
        unique.push(contact);
      } else {
        console.log(`🔄 Removed duplicate contact: ${name}`);
      }
    });
    
    return unique;
  };

  // Auto-categorize, remove duplicates, and sort contacts
  const categorizedContacts = removeDuplicates(icloudContacts)
    .map(contact => ({
      ...contact,
      category: categorizeContact(contact)
    }))
    .filter(contact => {
      // Filter out contacts with no name — check name field first, then first/last
      const displayName = (contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`).trim();
      return displayName && displayName.toLowerCase() !== 'unknown';
    })
    .sort((a, b) => {
      const nameA = (a.name || `${a.first_name || ''} ${a.last_name || ''}`).trim().toLowerCase();
      const nameB = (b.name || `${b.first_name || ''} ${b.last_name || ''}`).trim().toLowerCase();
      
      if (sortOrder === 'a-z') {
        return nameA.localeCompare(nameB);
      } else if (sortOrder === 'z-a') {
        return nameB.localeCompare(nameA);
      } else if (sortOrder === 'newest') {
        // Sort by created_at timestamp descending (newest first)
        // Falls back to synced_at if created_at not available
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 
                      (a.synced_at ? new Date(a.synced_at).getTime() : 0);
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 
                      (b.synced_at ? new Date(b.synced_at).getTime() : 0);
        return dateB - dateA;
      }
      return 0;
    });

  // Combine and filter contacts - handle Nylas, iCloud, and 'all' tabs
  const getDisplayContacts = () => {
    // Helper function to filter by category
    const filterByCategory = (contacts) => {
      if (selectedCategory === 'all') return contacts;
      return contacts.filter(c => c.category === selectedCategory);
    };
    
    // Helper function to filter by search
    const filterBySearch = (contacts) => {
      if (!searchQuery) return contacts;
      const searchLower = searchQuery.toLowerCase();
      return contacts.filter(c => {
        const name = `${c.given_name || c.first_name || ''} ${c.surname || c.last_name || ''}`.toLowerCase();
        const email = (c.emails?.[0]?.email || c.email || '').toLowerCase();
        const org = (c.company_name || c.organization || '').toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower) || org.includes(searchLower);
      });
    };
    
    // Handle 'icloud' tab specifically
    if (activeNylasAccount === 'icloud') {
      let contactsToShow = categorizedContacts;
      contactsToShow = filterByCategory(contactsToShow);
      contactsToShow = filterBySearch(contactsToShow);
      return contactsToShow;
    }
    
    // Handle 'all' tab - combine iCloud and Nylas contacts
    if (activeNylasAccount === 'all') {
      let allContacts = [];
      
      // Add iCloud contacts
      if (icloudSynced) {
        allContacts = [...categorizedContacts.map(c => ({ ...c, source: 'icloud' }))];
      }
      
      // Add Nylas contacts
      if (nylasAccounts.length > 0) {
        allContacts = [...allContacts, ...nylasContacts];
      }
      
      // Apply category filter
      allContacts = filterByCategory(allContacts);
      
      // Apply search filter
      allContacts = filterBySearch(allContacts);
      
      return allContacts;
    }
    
    // Handle specific Nylas account tab
    if (nylasAccounts.length > 0) {
      let contactsToShow = nylasContacts;
      
      // Filter by active account
      contactsToShow = nylasContacts.filter(c => c.account_email === activeNylasAccount);
      
      // Apply category filter
      contactsToShow = filterByCategory(contactsToShow);
      
      // Apply search filter
      contactsToShow = filterBySearch(contactsToShow);
      
      return contactsToShow;
    }
    
    // Fall back to iCloud/legacy contacts
    let contactsToShow = categorizedContacts;
    contactsToShow = filterByCategory(contactsToShow);
    contactsToShow = filterBySearch(contactsToShow);
    return contactsToShow;
  };

  // Filter contacts based on selected category and search query
  const filteredContacts = (nylasAccounts.length > 0 || icloudSynced)
    ? getDisplayContacts()
    : categorizedContacts.filter(c => {
        const matchesCategory = selectedCategory === 'all' || c.category === selectedCategory;
        
        if (!searchQuery) return matchesCategory;
        
        const searchLower = searchQuery.toLowerCase();
        const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
        const email = c.emails?.[0]?.email?.toLowerCase() || '';
        const org = c.organization?.toLowerCase() || '';
        
        return matchesCategory && (name.includes(searchLower) || email.includes(searchLower) || org.includes(searchLower));
      });

  // Pagination
  const totalPages = Math.ceil(filteredContacts.length / contactsPerPage);
  const startIndex = (currentPage - 1) * contactsPerPage;
  const endIndex = startIndex + contactsPerPage;
  const paginatedContacts = filteredContacts.slice(startIndex, endIndex);

  // Reset to page 1 when category or search changes
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleEditContact = () => {
    setIsEditMode(true);
    setEditedContact({ ...selectedContact });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedContact(null);
  };

  // Resize an image file to a compact base64 JPEG — stored directly in MongoDB, works on any deployment
  const resizeImageToBase64 = (file, maxDim = 400, quality = 0.75) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let w = img.width, h = img.height;
          if (w > maxDim || h > maxDim) {
            if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
            else { w = Math.round(w * maxDim / h); h = maxDim; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Shared helper: resize → save profile_image to DB → update local state
  const saveContactPhoto = async (file, contactId) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return null;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return null;
    }
    const dataUrl = await resizeImageToBase64(file);

    // Use POST /update-contact to avoid URL-encoding issues with special chars in iCloud IDs
    const updateResponse = await fetch(
      `${apiUrl}/api/icloud-contacts/update-contact`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: contactId, user_id: userId, profile_image: dataUrl })
      }
    );
    if (!updateResponse.ok) {
      let errMsg = 'Failed to save photo';
      try { const err = await updateResponse.json(); errMsg = err.detail || errMsg; } catch { /* ignored */ }
      throw new Error(errMsg);
    }
    return dataUrl;
  };

  // Handle contact profile image upload (from detail modal)
  const handleContactImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingContactImage(true);
    try {
      let contactId = selectedContact?.id || selectedContact?.icloud_id;
      const dataUrl = await saveContactPhoto(file, contactId);
      if (!dataUrl) return;

      if (editedContact) setEditedContact({ ...editedContact, profile_image: dataUrl });

      if (contactId) {
        setSelectedContact(prev => ({ ...prev, profile_image: dataUrl }));
        setIcloudContacts(prev => {
          const updated = prev.map(c =>
            (c.id === contactId || c.icloud_id === contactId)
              ? { ...c, profile_image: dataUrl }
              : c
          );
          localStorage.setItem(`icloud_contacts_${userId}`, JSON.stringify(updated));
          return updated;
        });
        toast.success('Profile photo saved permanently!');

        // Background verify from MongoDB
        try {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', `${apiUrl}/api/icloud-contacts/stored?user_id=${userId}`, true);
          xhr.onload = function() {
            if (xhr.status >= 200 && xhr.status < 300) {
              const data = JSON.parse(xhr.responseText);
              if (data.contacts && data.contacts.length > 0) {
                setIcloudContacts(data.contacts);
                localStorage.setItem(`icloud_contacts_${userId}`, JSON.stringify(data.contacts));
              }
            }
          };
          xhr.send();
        } catch { /* ignored */ }
      }
    } catch (error) {
      console.error('Error uploading contact image:', error);
      toast.error('Failed to update photo');
    } finally {
      setUploadingContactImage(false);
      if (contactImageInputRef.current) contactImageInputRef.current.value = '';
    }
  };

  // Handle photo upload directly from a contact card
  const handleCardPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !cardUploadTargetContact) return;

    const contactId = cardUploadTargetContact.id || cardUploadTargetContact.icloud_id;
    if (!contactId) return;

    setUploadingCardPhotoId(contactId);
    try {
      const dataUrl = await saveContactPhoto(file, contactId);
      if (!dataUrl) return;

      // Update local state immediately for instant feedback
      setIcloudContacts(prev => {
        const updated = prev.map(c =>
          (c.id === contactId || c.icloud_id === contactId)
            ? { ...c, profile_image: dataUrl }
            : c
        );
        localStorage.setItem(`icloud_contacts_${userId}`, JSON.stringify(updated));
        return updated;
      });
      toast.success('Photo saved permanently!');

      // Background verify: re-fetch from MongoDB to confirm persistence
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${apiUrl}/api/icloud-contacts/stored?user_id=${userId}`, true);
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText);
            if (data.contacts && data.contacts.length > 0) {
              setIcloudContacts(data.contacts);
              localStorage.setItem(`icloud_contacts_${userId}`, JSON.stringify(data.contacts));
            }
          }
        };
        xhr.send();
      } catch { /* ignored */ }
    } catch (error) {
      console.error('Error uploading card photo:', error);
      toast.error('Failed to update photo');
    } finally {
      setUploadingCardPhotoId(null);
      setCardUploadTargetContact(null);
      if (cardImageInputRef.current) cardImageInputRef.current.value = '';
    }
  };

  const handleSaveContact = async () => {
    // Check if this is a new contact or an existing one
    if (!selectedContact) {
      // This is a new contact - generate an ID and add to the list
      const newContact = {
        ...editedContact,
        icloud_id: `local-${Date.now()}`,
        id: `local-${Date.now()}`,
        created_at: new Date().toISOString(),
        source: 'manual'
      };
      
      // Save to backend FIRST
      try {
        const response = await fetch(`${apiUrl}/api/icloud-contacts/add?user_id=${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...newContact,
            user_id: userId
          })
        });
        
        if (response.ok) {
          const savedContact = await response.json();
          console.log('✅ Contact saved to backend:', savedContact);
          
          // Use the backend response to update local state (may have different ID)
          const contactToAdd = savedContact.contact || newContact;
          const updatedContacts = [...icloudContacts, contactToAdd];
          setIcloudContacts(updatedContacts);
          setIcloudSynced(true);
        } else {
          throw new Error('Backend save failed');
        }
      } catch (error) {
        console.error('⚠️ Could not save to backend:', error);
        // Still add locally as fallback
        const updatedContacts = [...icloudContacts, newContact];
        setIcloudContacts(updatedContacts);
        setIcloudSynced(true);
      }
      
      // Close modal
      setIsDetailModalOpen(false);
      setIsEditMode(false);
      setEditedContact(null);
      
      // Show success toast with category info
      const categoryName = categories.find(c => c.id === newContact.category)?.name || newContact.category;
      toast.success(`Contact "${newContact.first_name} ${newContact.last_name}" added to ${categoryName}!`);
      
      console.log('✅ New contact added successfully');
      return;
    }
    
    // IMPORTANT: Save to backend FIRST for persistence
    try {
      const contactId = editedContact.icloud_id || editedContact.id;
      console.log('📝 Saving contact with ID:', contactId);
      console.log('📝 Contact data:', JSON.stringify({
        first_name: editedContact.first_name,
        last_name: editedContact.last_name,
        category: editedContact.category
      }));
      
      // Use POST with contact_id in body to handle IDs with special characters (slashes)
      const response = await fetch(`${apiUrl}/api/icloud-contacts/update-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_id: contactId,
          user_id: userId,
          first_name: editedContact.first_name,
          last_name: editedContact.last_name,
          category: editedContact.category,
          organization: editedContact.organization,
          job_title: editedContact.job_title,
          phones: editedContact.phones,
          emails: editedContact.emails,
          location: editedContact.location,
          linkedin_url: editedContact.linkedin_url,
          notes: editedContact.notes
        })
      });
      
      console.log('📝 Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('⚠️ Backend update failed:', response.status, errorText);
        toast.error(`Failed to save changes. Error: ${response.status}`);
        return;
      }
      
      const result = await response.json();
      console.log('✅ Contact updated in backend:', result);
      
      // Update local state with the saved data
      const savedContact = result.contact || editedContact;
      const updatedContacts = icloudContacts.map(contact => 
        (contact.icloud_id === contactId || contact.id === contactId) ? savedContact : contact
      );
      
      setIcloudContacts(updatedContacts);
      setSelectedContact(savedContact);
      setIsEditMode(false);
      
      // Show success toast with category info
      const categoryName = categories.find(c => c.id === savedContact.category)?.name || savedContact.category;
      toast.success(`Contact updated! Category: ${categoryName}`);
      
      setEditedContact(null);
      console.log('✅ Contact updated successfully');
      
    } catch (error) {
      console.error('⚠️ Error saving contact:', error);
      toast.error('Failed to save changes. Please try again.');
    }
  };

  const handleDeleteContact = async () => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete ${selectedContact.first_name || ''} ${selectedContact.last_name || ''}? This cannot be undone.`)) {
      return;
    }
    
    try {
      // Call API to permanently delete from database
      const contactId = selectedContact.icloud_id || selectedContact.id;
      
      // Use XMLHttpRequest to avoid response body issues
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('DELETE', `${apiUrl}/api/icloud-contacts/delete/${encodeURIComponent(contactId)}?user_id=${userId}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              resolve({ success: true });
            }
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(new Error(error.detail || 'Failed to delete contact'));
            } catch {
              reject(new Error(`HTTP ${xhr.status}: Failed to delete contact`));
            }
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error while deleting contact'));
        };
        
        xhr.send();
      });
      
      console.log('✅ Delete API response:', result);
      
      // Remove contact from state
      const updatedContacts = icloudContacts.filter(contact => 
        contact.icloud_id !== selectedContact.icloud_id && contact.id !== selectedContact.id
      );
      
      setIcloudContacts(updatedContacts);
      
      // Update in localStorage
      localStorage.setItem(`icloud_contacts_${userId}`, JSON.stringify(updatedContacts));
      
      // Close modal
      setIsDetailModalOpen(false);
      setSelectedContact(null);
      setIsEditMode(false);
      setEditedContact(null);
      
      console.log('✅ Contact permanently deleted from database');
    } catch (error) {
      console.error('❌ Error deleting contact:', error);
      alert(`Failed to delete contact: ${error.message}`);
    }
  };
  
  const handleLinkedInScan = async () => {
    if (!selectedContact) return;
    
    setIsScanning(true);
    setLinkedinImportedData(null); // Clear previous import data
    console.log('🔍 Starting LinkedIn scan for:', selectedContact.first_name, selectedContact.last_name);
    
    try {
      const searchName = `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim();
      const searchCompany = selectedContact.organization || '';
      
      // Call LinkedIn scraper API
      const response = await fetch(`${apiUrl}/api/linkedin/scrape`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: searchName,
          company: searchCompany
        })
      });
      
      if (!response.ok) {
        throw new Error('LinkedIn scan failed');
      }
      
      const result = await response.json();
      console.log('✅ LinkedIn API response:', result);
      
      // Extract the data from the response wrapper
      const data = result.data || result;
      const rawData = data.raw_data || {};
      
      // Check if we got any useful data - check both data and raw_data
      const hasData = data.name || data.job_title || data.company || data.bio || data.location || 
                      rawData.fullName || rawData.headline || rawData.companyName ||
                      (data.skills && data.skills.length > 0) || (data.experience && data.experience.length > 0);
      
      if (!hasData) {
        // Check for error in raw_data
        const errorMsg = rawData.error || 'No LinkedIn profile found for this person. Try entering their LinkedIn URL manually.';
        setLinkedinImportedData({ 
          error: true, 
          message: errorMsg,
          searchedName: searchName,
          searchedCompany: searchCompany
        });
        return;
      }
      
      // Parse the name - try multiple sources
      const fullName = data.name || rawData.fullName || '';
      let firstName = rawData.firstName || fullName.split(' ')[0] || '';
      let lastName = rawData.lastName || fullName.split(' ').slice(1).join(' ') || '';
      
      // Extract company from multiple sources
      const company = data.company || rawData.companyName || '';
      
      // Extract location from multiple sources
      const location = data.location || rawData.addressCountryOnly || rawData.jobLocation || '';
      
      // Extract job title from multiple sources
      const jobTitle = data.job_title || rawData.headline || rawData.jobTitle || '';
      
      // Extract company website
      const companyWebsite = rawData.companyWebsite || rawData.companyUrl || 
                            (rawData.websites?.length > 0 ? rawData.websites[0] : '');
      
      // Extract education - format nicely
      const educationArray = data.education || rawData.education || [];
      const educationText = Array.isArray(educationArray) ? 
        educationArray.map(edu => 
          `${edu.schoolName || edu.school || edu.name || ''}${edu.degree || edu.degreeName ? ` - ${edu.degree || edu.degreeName}` : ''}`
        ).filter(Boolean).join('; ') : '';
      
      // Store the imported data to display in UI
      const importedFields = {};
      if (firstName && firstName !== selectedContact.first_name) importedFields.first_name = firstName;
      if (lastName && lastName !== selectedContact.last_name) importedFields.last_name = lastName;
      if (company && company !== selectedContact.organization) importedFields.organization = company;
      if (jobTitle && jobTitle !== selectedContact.job_title) importedFields.job_title = jobTitle;
      if (location && location !== selectedContact.location) importedFields.location = location;
      if (rawData.linkedinUrl) importedFields.linkedin_url = rawData.linkedinUrl;
      if (data.profile_picture) importedFields.profile_image = data.profile_picture;
      if (data.bio) importedFields.about = data.bio;
      if (data.skills && data.skills.length > 0) importedFields.skills = data.skills;
      if (data.experience && data.experience.length > 0) importedFields.experience = data.experience;
      if (educationText) importedFields.education = educationText;
      if (data.email || rawData.email) importedFields.email = data.email || rawData.email;
      if (companyWebsite) importedFields.company_website = companyWebsite;
      
      console.log('📋 Imported fields:', importedFields);
      
      setLinkedinImportedData({
        raw: data,
        fields: importedFields,
        fieldCount: Object.keys(importedFields).length
      });
      
      const updatedContact = {
        ...(selectedContact || {}),
        first_name: firstName || selectedContact?.first_name || '',
        last_name: lastName || selectedContact?.last_name || '',
        organization: company || selectedContact?.organization || '',
        job_title: jobTitle || selectedContact?.job_title || '',
        location: location || selectedContact?.location || '',
        linkedin_url: rawData.linkedinUrl || selectedContact?.linkedin_url || '',
        profile_image: data.profile_picture || selectedContact?.profile_image || '',
        about: data.bio || selectedContact?.about || '',
        skills: data.skills || selectedContact?.skills || [],
        experience: data.experience || selectedContact?.experience || [],
        education: educationText || selectedContact?.education || '',
        company_website: companyWebsite || selectedContact?.company_website || '',
        // Preserve existing contact fields
        emails: selectedContact?.emails || [],
        phones: selectedContact?.phones || [],
        notes: selectedContact?.notes || '',
      };
      
      console.log('📝 Updated contact:', updatedContact);
      
      // IMPORTANT: Set editedContact FIRST, then set edit mode
      setEditedContact(updatedContact);
      setSelectedContact(updatedContact);
      // Update the LinkedIn URL input field
      if (rawData.linkedinUrl) {
        setLinkedinUrlInput(rawData.linkedinUrl);
      }
      
      // Now enable edit mode - the form will have data to display
      if (!isEditMode) {
        setIsEditMode(true);
      }
      
    } catch (error) {
      console.error('❌ LinkedIn scan error:', error);
      setLinkedinImportedData({ error: true, message: 'Failed to scan LinkedIn. Please try again or enter details manually.' });
    } finally {
      setIsScanning(false);
    }
  };

  // Scan a specific LinkedIn URL
  const handleLinkedInUrlScan = async (url) => {
    if (!url || !url.includes('linkedin.com')) {
      setLinkedinImportedData({ error: true, message: 'Please enter a valid LinkedIn URL (e.g., https://linkedin.com/in/username)' });
      return;
    }
    
    setIsScanning(true);
    setLinkedinImportedData(null);
    console.log('🔍 Scanning LinkedIn URL:', url);
    
    try {
      const response = await fetch(`${apiUrl}/api/linkedin/scrape/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_url: url })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to scan LinkedIn profile');
      }
      
      const result = await response.json();
      console.log('✅ LinkedIn profile data:', result);
      
      const data = result.data || result;
      const rawData = data.raw_data || {};
      
      // Even if profile is private, we can still save the URL and any basic public data
      const hasAnyData = data.name || data.job_title || data.profile_picture || data.location || 
                         rawData.fullName || rawData.headline || rawData.companyName;
      
      // Parse the name - try multiple sources
      const fullName = data.name || rawData.fullName || '';
      let firstName = rawData.firstName || fullName.split(' ')[0] || '';
      let lastName = rawData.lastName || fullName.split(' ').slice(1).join(' ') || '';
      
      // Extract company from multiple sources
      const company = data.company || rawData.companyName || '';
      
      // Extract location from multiple sources
      const location = data.location || rawData.addressCountryOnly || rawData.jobLocation || '';
      
      // Extract job title from multiple sources
      const jobTitle = data.job_title || rawData.headline || rawData.jobTitle || '';
      
      // Extract company website
      const companyWebsite = rawData.companyWebsite || rawData.companyUrl || 
                            (rawData.websites?.length > 0 ? rawData.websites[0] : '');
      
      // Extract education - format nicely
      const educationArray = data.education || rawData.education || [];
      const educationText = Array.isArray(educationArray) ? 
        educationArray.map(edu => 
          `${edu.schoolName || edu.school || edu.name || ''}${edu.degree || edu.degreeName ? ` - ${edu.degree || edu.degreeName}` : ''}`
        ).filter(Boolean).join('; ') : '';
      
      // Store imported fields
      const importedFields = {};
      if (firstName) importedFields.first_name = firstName;
      if (lastName) importedFields.last_name = lastName;
      if (company) importedFields.organization = company;
      if (jobTitle) importedFields.job_title = jobTitle;
      if (location) importedFields.location = location;
      importedFields.linkedin_url = url; // Always save the URL
      if (data.profile_picture) importedFields.profile_image = data.profile_picture;
      if (data.bio) importedFields.about = data.bio;
      if (data.skills?.length > 0) importedFields.skills = data.skills;
      if (data.experience?.length > 0) importedFields.experience = data.experience;
      if (educationText) importedFields.education = educationText;
      if (data.email || rawData.email) importedFields.email = data.email || rawData.email;
      if (companyWebsite) importedFields.company_website = companyWebsite;
      
      // If we only got the URL (profile is very private), still count it as success
      if (!hasAnyData) {
        importedFields.linkedin_url = url;
      }
      
      console.log('📋 Imported fields:', importedFields);
      
      setLinkedinImportedData({
        raw: data,
        fields: importedFields,
        fieldCount: Object.keys(importedFields).length,
        partialData: !fullName && !jobTitle
      });
      
      // Build updated contact with LinkedIn data
      const updatedContact = {
        ...(selectedContact || {}),
        first_name: firstName || selectedContact?.first_name || '',
        last_name: lastName || selectedContact?.last_name || '',
        organization: company || selectedContact?.organization || '',
        job_title: jobTitle || selectedContact?.job_title || '',
        location: location || selectedContact?.location || '',
        linkedin_url: url,
        profile_image: data.profile_picture || selectedContact?.profile_image || '',
        about: data.bio || selectedContact?.about || '',
        skills: data.skills || selectedContact?.skills || [],
        experience: data.experience || selectedContact?.experience || [],
        education: educationText || selectedContact?.education || '',
        company_website: companyWebsite || selectedContact?.company_website || '',
        // Preserve existing contact fields
        emails: selectedContact?.emails || [],
        phones: selectedContact?.phones || [],
        notes: selectedContact?.notes || '',
      };
      
      console.log('📝 Updated contact:', updatedContact);
      
      // Set editedContact FIRST, then set edit mode
      setEditedContact(updatedContact);
      setSelectedContact(updatedContact);
      setLinkedinUrlInput(url);
      
      // Now enable edit mode - the form will have data to display
      if (!isEditMode) setIsEditMode(true);
      
    } catch (error) {
      console.error('❌ LinkedIn URL scan error:', error);
      setLinkedinImportedData({ error: true, message: error.message || 'Failed to scan LinkedIn profile.' });
    } finally {
      setIsScanning(false);
    }
  };

  // Auto-search for LinkedIn profile based on contact info
  const handleAutoSearchLinkedIn = async () => {
    if (!selectedContact) return;
    
    setIsSearchingLinkedIn(true);
    setLinkedinImportedData(null);
    
    const searchName = `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim();
    const searchCompany = selectedContact.organization || '';
    const searchEmail = selectedContact.emails?.[0]?.email || '';
    
    console.log('🔍 Auto-searching LinkedIn for:', { searchName, searchCompany, searchEmail });
    
    try {
      // Use the existing name-based search endpoint
      const response = await fetch(`${apiUrl}/api/linkedin/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: searchName, company: searchCompany })
      });
      
      if (!response.ok) {
        throw new Error('Search failed');
      }
      
      const result = await response.json();
      const data = result.data || result;
      
      // Check if we found a URL
      const foundUrl = data.raw_data?.linkedinUrl;
      
      if (foundUrl) {
        setLinkedinUrlInput(foundUrl);
        setLinkedinImportedData({
          found: true,
          message: `Found potential LinkedIn profile! Click "Scan URL" to fetch the full profile.`,
          url: foundUrl
        });
      } else {
        setLinkedinImportedData({
          error: true,
          message: 'No LinkedIn profile found. Try entering the URL manually.',
          searchedName: searchName,
          searchedCompany: searchCompany
        });
      }
      
    } catch (error) {
      console.error('❌ Auto-search error:', error);
      setLinkedinImportedData({ error: true, message: 'Auto-search failed. Please enter the LinkedIn URL manually.' });
    } finally {
      setIsSearchingLinkedIn(false);
    }
  };

  const updateEditedField = (field, value) => {
    setEditedContact({ ...editedContact, [field]: value });
  };

  const updateEditedEmail = (index, value) => {
    const newEmails = [...(editedContact.emails || [])];
    newEmails[index] = { ...newEmails[index], email: value };
    setEditedContact({ ...editedContact, emails: newEmails });
  };

  const updateEditedPhone = (index, value) => {
    const newPhones = [...(editedContact.phones || [])];
    newPhones[index] = { ...newPhones[index], number: value };
    setEditedContact({ ...editedContact, phones: newPhones });
  };

  const addEmail = () => {
    const newEmails = [...(editedContact.emails || []), { email: '', type: 'personal' }];
    setEditedContact({ ...editedContact, emails: newEmails });
  };

  const addPhone = () => {
    const newPhones = [...(editedContact.phones || []), { number: '', type: 'mobile' }];
    setEditedContact({ ...editedContact, phones: newPhones });
  };

  const removeEmail = (index) => {
    const newEmails = editedContact.emails.filter((_, i) => i !== index);
    setEditedContact({ ...editedContact, emails: newEmails });
  };

  const removePhone = (index) => {
    const newPhones = editedContact.phones.filter((_, i) => i !== index);
    setEditedContact({ ...editedContact, phones: newPhones });
  };

  return (
    <div className="min-h-screen p-3 md:p-8 flex flex-col overflow-hidden bg-black">
      {/* Page Header - At the very top */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">Contacts</h1>
        {isAuthenticated && (
          <button 
            onClick={handleSync}
            disabled={syncing || authenticating}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-3 md:px-6 py-2 md:py-3 rounded-lg font-semibold flex items-center justify-center gap-1 md:gap-2 transition-all text-xs md:text-base"
          >
            {syncing || authenticating ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                <span className="hidden sm:inline">{authenticating ? 'Connecting...' : 'Syncing...'}</span>
                <span className="sm:hidden">...</span>
              </>
            ) : (
              <>
                <Cloud size={16} />
                <span className="hidden sm:inline">Refresh iCloud</span>
                <span className="sm:hidden">Sync</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Main Contact Type Tabs */}
      <div className="mb-4 flex items-center gap-2">
        <button
          onClick={() => setMainContactTab('personal')}
          className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
            mainContactTab === 'personal'
              ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg shadow-purple-500/25'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
          }`}
          data-testid="main-tab-personal"
        >
          <Users size={18} />
          All Personal Contacts
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            mainContactTab === 'personal' ? 'bg-white/20' : 'bg-gray-700'
          }`}>
            {nylasContacts.length + (icloudSynced ? icloudContactsCount : 0)}
          </span>
        </button>
        <button
          onClick={() => setMainContactTab('leads')}
          className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
            mainContactTab === 'leads'
              ? 'bg-gradient-to-r from-cyan-600 to-emerald-600 text-white shadow-lg shadow-cyan-500/25'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
          }`}
          data-testid="main-tab-leads"
        >
          <Briefcase size={18} />
          All Lead Contacts
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            mainContactTab === 'leads' ? 'bg-white/20' : 'bg-gray-700'
          }`}>
            {leadContacts.length}
          </span>
        </button>
      </div>

      {/* Lead Contacts View */}
      {mainContactTab === 'leads' ? (
        <div className="flex-1 flex flex-col">
          {/* Category Tabs for Lead Contacts - Synced with Lead Gen page */}
          <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-2">
            {leadCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedLeadCategory(cat.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap flex items-center gap-1 border ${
                  selectedLeadCategory === cat.id
                    ? 'bg-white text-black border-white'
                    : 'bg-black text-white border-white hover:bg-gray-900'
                }`}
              >
                {cat.label || cat.name}
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${
                  selectedLeadCategory === cat.id ? 'bg-black/20' : 'bg-gray-800'
                }`}>
                  {cat.id === 'all' 
                    ? leadContacts.length 
                    : leadContacts.filter(c => c.company_industry?.toLowerCase().replace(/\s+/g, '-') === cat.id).length
                  }
                </span>
              </button>
            ))}
            {/* Manage Categories Button */}
            <button
              onClick={() => setShowManageLeadCategories(true)}
              className="px-3 py-2 rounded-lg font-medium transition-all whitespace-nowrap bg-black text-white border border-dashed border-white hover:bg-gray-900"
              title="Manage category tabs"
            >
              <Settings size={16} />
            </button>
          </div>

          {/* Search Bar and Filters for Lead Contacts */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white" size={18} />
              <input
                type="text"
                placeholder="Search lead contacts..."
                value={leadContactsSearchQuery}
                onChange={(e) => setLeadContactsSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-black border border-white rounded-xl text-white placeholder-gray-400 focus:border-gray-300 outline-none"
              />
            </div>
            
            {/* Sort Dropdown */}
            <select
              value={leadContactsSortBy}
              onChange={(e) => setLeadContactsSortBy(e.target.value)}
              className="px-3 py-2.5 bg-black border border-white rounded-xl text-white text-sm focus:border-gray-300 outline-none cursor-pointer"
            >
              <option value="company-az">Company A-Z</option>
              <option value="company-za">Company Z-A</option>
              <option value="contact-az">Contact A-Z</option>
              <option value="contact-za">Contact Z-A</option>
              <option value="date-newest">Date Added (Newest)</option>
              <option value="date-oldest">Date Added (Oldest)</option>
            </select>
            
            <button
              onClick={fetchLeadContacts}
              className="p-2.5 bg-black border border-white hover:bg-gray-900 rounded-xl text-white transition-colors"
              title="Refresh lead contacts"
            >
              <RefreshCw size={18} className={loadingLeadContacts ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* Lead Contacts Grid - Grouped by Company */}
          {loadingLeadContacts ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
            </div>
          ) : leadContacts.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
              <Briefcase className="w-16 h-16 text-gray-600 mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">No Lead Contacts Yet</h3>
              <p className="text-gray-400 max-w-md">
                Add companies with contacts on the Lead Generation page and they'll appear here automatically.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-y-auto pb-4">
              {leadContacts
                .filter(companyCard => {
                  // Filter by category
                  if (selectedLeadCategory !== 'all') {
                    const cardCatId = companyCard.company_industry?.toLowerCase().replace(/\s+/g, '-');
                    if (cardCatId !== selectedLeadCategory) return false;
                  }
                  // Filter by search
                  if (!leadContactsSearchQuery) return true;
                  const searchLower = leadContactsSearchQuery.toLowerCase();
                  const companyName = (companyCard.company_name || '').toLowerCase();
                  const contactsMatch = companyCard.contacts?.some(c => {
                    const name = (c.name || `${c.first_name || ''} ${c.last_name || ''}`).toLowerCase();
                    const email = (c.email || '').toLowerCase();
                    return name.includes(searchLower) || email.includes(searchLower);
                  });
                  return companyName.includes(searchLower) || contactsMatch;
                })
                .sort((a, b) => {
                  // Apply sorting based on leadContactsSortBy
                  switch (leadContactsSortBy) {
                    case 'company-az':
                      return (a.company_name || '').localeCompare(b.company_name || '');
                    case 'company-za':
                      return (b.company_name || '').localeCompare(a.company_name || '');
                    case 'contact-az': {
                      const aName = a.contacts?.[0]?.name || `${a.contacts?.[0]?.first_name || ''} ${a.contacts?.[0]?.last_name || ''}`.trim() || '';
                      const bName = b.contacts?.[0]?.name || `${b.contacts?.[0]?.first_name || ''} ${b.contacts?.[0]?.last_name || ''}`.trim() || '';
                      return aName.localeCompare(bName);
                    }
                    case 'contact-za': {
                      const aName = a.contacts?.[0]?.name || `${a.contacts?.[0]?.first_name || ''} ${a.contacts?.[0]?.last_name || ''}`.trim() || '';
                      const bName = b.contacts?.[0]?.name || `${b.contacts?.[0]?.first_name || ''} ${b.contacts?.[0]?.last_name || ''}`.trim() || '';
                      return bName.localeCompare(aName);
                    }
                    case 'date-newest':
                      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                    case 'date-oldest':
                      return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                    default:
                      return 0;
                  }
                })
                .map((companyCard) => (
                <div
                  key={companyCard.company_id}
                  className="bg-black border border-white rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_30px_rgba(168,85,247,0.4)] transition-all duration-300"
                >
                  {/* Company Info Header */}
                  <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-600">
                    {companyCard.company_logo ? (
                      <img src={companyCard.company_logo} alt="" className="w-10 h-10 rounded-lg object-contain bg-white p-1" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                        <Building2 size={20} className="text-gray-500" />
                      </div>
                    )}
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleEditLeadCompany(companyCard)}
                    >
                      <h4 className="text-cyan-400 font-medium truncate hover:text-cyan-300">{companyCard.company_name || 'No Company'}</h4>
                      {companyCard.company_industry && (
                        <span className="text-xs text-gray-500">{companyCard.company_industry}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded">
                        {companyCard.contacts?.length || 0} contact{(companyCard.contacts?.length || 0) !== 1 ? 's' : ''}
                      </div>
                      {/* Send to Queue Button */}
                      {companyCard.contacts?.some(c => c.email) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Get all email contacts from this company
                            const emailContacts = companyCard.contacts?.filter(c => c.email) || [];
                            if (emailContacts.length === 0) {
                              alert('No contacts with email addresses found');
                              return;
                            }
                            // Store company data in localStorage for the Lead Gen page to pick up
                            const queueData = {
                              company_id: companyCard.company_id,
                              company_name: companyCard.company_name,
                              contacts: emailContacts.map(c => ({
                                id: c.id,
                                name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                                email: c.email,
                                title: c.title
                              })),
                              added_at: new Date().toISOString()
                            };
                            // Add to queue in localStorage
                            const existingQueue = JSON.parse(localStorage.getItem('lead_email_queue') || '[]');
                            // Check if company already in queue
                            if (!existingQueue.some(q => q.company_id === companyCard.company_id)) {
                              existingQueue.push(queueData);
                              localStorage.setItem('lead_email_queue', JSON.stringify(existingQueue));
                            }
                            // Navigate to Lead Gen page with queue tab
                            window.location.href = '/lead-generation?tab=queue';
                          }}
                          className="p-1.5 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-white transition-colors"
                          title="Send to Email Queue"
                        >
                          <Send size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* All Contacts for this Company - scrollable if more than 3 */}
                  <div className={`space-y-3 ${companyCard.contacts?.length > 3 ? 'max-h-[200px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent' : ''}`}>
                    {companyCard.contacts?.map((contact, idx) => (
                      <div key={contact.id || idx} className={`flex items-start gap-3 ${idx > 0 ? 'pt-3 border-t border-gray-600' : ''}`}>
                        {/* Avatar */}
                        <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-xs">
                          {contact.name?.charAt(0) || contact.first_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* Name */}
                          <h5 className="text-white font-medium text-sm truncate">
                            {contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown'}
                          </h5>
                          {/* Title */}
                          {contact.title && (
                            <p className="text-gray-500 text-xs truncate">{contact.title}</p>
                          )}
                          {/* Email & Phone */}
                          <div className="flex flex-wrap gap-2 mt-1">
                            {contact.email && (
                              <a 
                                href={`mailto:${contact.email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 text-xs text-gray-400 hover:text-cyan-400 transition-colors"
                              >
                                <Mail size={10} />
                                <span className="truncate max-w-[120px]">{contact.email}</span>
                              </a>
                            )}
                            {contact.phone && (
                              <a 
                                href={`tel:${contact.phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 text-xs text-gray-400 hover:text-green-400 transition-colors"
                              >
                                <Phone size={10} />
                                <span>{contact.phone}</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Show if no contacts */}
                    {(!companyCard.contacts || companyCard.contacts.length === 0) && (
                      <p className="text-gray-500 text-sm text-center py-2">No contacts added</p>
                    )}
                  </div>
                  
                  {/* Location */}
                  {companyCard.company_location && (
                    <div className="mt-3 pt-3 border-t border-gray-600 flex items-center gap-2 text-xs text-gray-500">
                      <MapPin size={12} />
                      <span className="truncate">{companyCard.company_location}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Personal Contacts View - Original Content */
        <>
      {/* Connected Contacts Tabs - Collapsible */}
      <div className="mb-4 bg-gradient-to-r from-purple-900/50 to-blue-900/50 border border-purple-500/30 rounded-xl overflow-hidden" data-testid="contacts-accounts-panel">
        {/* Collapsible Header */}
        <button
          onClick={() => setIsAccountsCollapsed(!isAccountsCollapsed)}
          className="w-full p-4 flex items-center justify-between hover:bg-purple-900/30 transition-colors"
          data-testid="contacts-accounts-collapse-toggle"
        >
          <div className="flex items-center gap-2">
            <Users className="text-purple-400" size={20} />
            <h2 className="text-base font-bold text-white">Contact Accounts</h2>
            <span className="text-xs text-gray-400">
              ({nylasAccounts.length + (icloudSynced ? 1 : 0)} connected)
            </span>
          </div>
          <ChevronDown 
            size={18} 
            className={`text-gray-400 transition-transform duration-200 ${isAccountsCollapsed ? '-rotate-90' : ''}`} 
          />
        </button>
        
        {/* Collapsible Content */}
        <div className={`transition-all duration-200 ease-in-out overflow-hidden ${isAccountsCollapsed ? 'max-h-0 opacity-0' : 'max-h-[500px] opacity-100'}`}>
          <div className="px-4 pb-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">Connect iCloud or other accounts</span>
                <div className="flex items-center gap-2">
                  {/* Quick Connect iCloud Button - always visible */}
                  {!icloudSynced && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); setShowCredentialsModal(true); }}
                      className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
                      data-testid="quick-connect-icloud"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      Connect iCloud
                    </button>
                  )}
                  <button 
                    onClick={(e) => { e.stopPropagation(); setShowManageAccountsModal(true); }}
                    className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1 transition-colors"
                  >
                    Manage Accounts
                    <ExternalLink size={12} />
                  </button>
                </div>
              </div>
            
              {/* Contact Account Tabs */}
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="contacts-account-tabs">
              {/* All Contacts Tab */}
              <button
                onClick={() => setActiveNylasAccount('all')}
                className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  activeNylasAccount === 'all'
                    ? 'bg-purple-600 text-white border-2 border-purple-400'
                    : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                }`}
                data-testid="contacts-tab-all"
              >
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                All Contacts
                <span className={`text-[10px] md:text-xs ${activeNylasAccount === 'all' ? 'text-white/80' : 'text-gray-500'}`}>
                  ({nylasContacts.length + (icloudSynced ? icloudContactsCount : 0)})
                </span>
              </button>
              
              {/* iCloud Tab - Always show, with connect option if not synced */}
              <div className="relative group flex items-center">
                <button
                  onClick={() => {
                    if (icloudSynced) {
                      setActiveNylasAccount('icloud');
                    } else {
                      setShowCredentialsModal(true);
                    }
                  }}
                  className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                    activeNylasAccount === 'icloud'
                      ? 'bg-blue-600 text-white border-2 border-blue-400'
                      : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                  }`}
                  data-testid="contacts-tab-icloud"
                  title={icloudSynced ? "View iCloud Contacts" : "Connect iCloud to sync contacts"}
                >
                  {/* Apple Icon */}
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span>iCloud</span>
                  {icloudSynced ? (
                    <span className={`text-[10px] md:text-xs ${activeNylasAccount === 'icloud' ? 'text-white/80' : 'text-gray-500'}`}>
                      ({icloudContactsCount})
                    </span>
                  ) : (
                    <span className="text-[10px] md:text-xs text-blue-400">Connect</span>
                  )}
                </button>
                {/* Action buttons for iCloud - appears on hover when synced */}
                {icloudSynced && (
                  <div className="absolute -top-1 -right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    {/* Sync button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSync();
                      }}
                      disabled={syncing || authenticating}
                      className="w-5 h-5 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center"
                      title="Sync iCloud Contacts"
                      data-testid="sync-icloud-contacts"
                    >
                      <RefreshCw size={10} className={syncing || authenticating ? 'animate-spin' : ''} />
                    </button>
                    {/* Disconnect button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDisconnectIcloudModal(true);
                      }}
                      className="w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center"
                      title="Disconnect iCloud"
                      data-testid="disconnect-icloud-contacts"
                    >
                      <X size={10} />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Individual Nylas Account Tabs */}
              {nylasAccounts.map((account) => {
                const accountContactCount = nylasContacts.filter(c => c.account_email === account.email).length;
                const isActive = activeNylasAccount === account.email;
                
                return (
                  <div key={account.email} className="relative group flex items-center">
                    <button
                      onClick={() => setActiveNylasAccount(account.email)}
                      className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                        isActive
                          ? 'bg-purple-600 text-white border-2 border-purple-400'
                          : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                      }`}
                      data-testid={`contacts-tab-${account.email}`}
                      title={account.email}
                    >
                      {/* Provider Icon */}
                      {account.provider === 'google' ? (
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      ) : account.provider === 'microsoft' || account.provider === 'outlook' ? (
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0078D4">
                          <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                        </svg>
                      ) : (
                        <span className="w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center text-[10px] text-white font-bold">
                          {account.email.charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span className="max-w-[120px] md:max-w-[180px] truncate">
                        {account.account_name || account.email.split('@')[0]}
                      </span>
                      <span className={`text-[10px] md:text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                        ({accountContactCount})
                      </span>
                    </button>
                    {/* Delete button - appears on hover */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteContactAccount(account);
                      }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      title={`Disconnect ${account.email}`}
                      data-testid={`delete-contacts-${account.email}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })}
              
              {/* Add Contact Account Button */}
              <button
                onClick={handleConnectContactAccount}
                className="px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap bg-gray-800 border border-dashed border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white hover:border-gray-500"
                title="Connect new contact account via Nylas"
                data-testid="add-contact-account-btn"
              >
                <Plus size={14} />
                <span className="hidden sm:inline">Add Account</span>
              </button>
            </div>
            
            {/* Loading/Status Indicator */}
            {loadingNylasContacts ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <Loader2 size={14} className="animate-spin text-purple-400" />
                <span className="text-xs text-purple-400">Loading contacts...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                <CheckCircle size={14} className="text-green-400" />
                <span className="text-xs text-green-400">
                  {activeNylasAccount === 'all' 
                    ? `${nylasContacts.length + icloudContactsCount} contacts from ${nylasAccounts.length + (icloudSynced ? 1 : 0)} account(s)`
                    : activeNylasAccount === 'icloud'
                      ? `${icloudContactsCount} contacts from iCloud`
                      : `${nylasContacts.filter(c => c.account_email === activeNylasAccount).length} contacts from ${activeNylasAccount}`
                  }
                </span>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {syncStatus && (
        <div className={`mb-3 md:mb-4 p-2 md:p-3 rounded-lg text-xs md:text-base ${syncStatus.includes('✅') ? 'bg-green-900/50 text-green-200' : syncStatus.includes('🔄') ? 'bg-blue-900/50 text-blue-200' : 'bg-red-900/50 text-red-200'}`}>
          {syncStatus}
        </div>
      )}

      {/* Main Container */}
      <div className="mb-4 bg-black border border-gray-400 rounded-xl p-2 md:p-3 flex-1 overflow-hidden flex flex-col hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 gap-2">
          <div className="flex items-center gap-2 md:gap-4 flex-wrap">
            <div className="flex items-center gap-1 md:gap-2">
              <Building2 size={18} className="text-white" />
              <span className="text-white font-semibold text-sm md:text-lg">All Contacts</span>
              <span className="px-2 md:px-3 py-0.5 md:py-1 rounded-full bg-white bg-opacity-20 text-[10px] md:text-xs font-bold text-white">
                {filteredContacts.length}
              </span>
            </div>
            
            {/* Showing X-Y of Z */}
            <span className="text-gray-400 text-xs md:text-sm">
              {filteredContacts.length > 0 ? (
                <>Showing {startIndex + 1}-{Math.min(endIndex, filteredContacts.length)} of {filteredContacts.length}</>
              ) : (
                <>No contacts</>
              )}
            </span>
            
            {/* Sort Filter */}
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-2 md:px-3 py-1 md:py-1.5 bg-gray-800 text-white border border-gray-600 rounded-lg text-[10px] md:text-xs focus:outline-none focus:border-blue-500"
            >
              <option value="a-z">A to Z</option>
              <option value="z-a">Z to A</option>
              <option value="newest">Newest</option>
            </select>

            {/* View Mode Toggle: Grid / List */}
            <div className="inline-flex bg-gray-800 border border-gray-600 rounded-lg overflow-hidden" data-testid="contacts-view-toggle">
              <button
                type="button"
                onClick={() => setContactsViewMode('grid')}
                className={`px-2 py-1 md:py-1.5 transition-colors flex items-center gap-1 ${
                  contactsViewMode === 'grid' ? 'bg-white text-black' : 'text-gray-300 hover:bg-gray-700'
                }`}
                data-testid="contacts-view-grid-btn"
                title="Grid view"
              >
                <LayoutGrid size={12} />
                <span className="hidden md:inline text-[10px] font-medium">Grid</span>
              </button>
              <button
                type="button"
                onClick={() => setContactsViewMode('list')}
                className={`px-2 py-1 md:py-1.5 transition-colors flex items-center gap-1 ${
                  contactsViewMode === 'list' ? 'bg-white text-black' : 'text-gray-300 hover:bg-gray-700'
                }`}
                data-testid="contacts-view-list-btn"
                title="List view"
              >
                <List size={12} />
                <span className="hidden md:inline text-[10px] font-medium">List</span>
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full sm:w-48 md:w-80 px-3 md:px-4 py-1.5 md:py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-xs md:text-sm"
            />
            <button 
              className="bg-white hover:bg-gray-200 text-black px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-semibold flex items-center justify-center gap-1 md:gap-2 transition-all text-xs md:text-sm whitespace-nowrap"
              onClick={() => {
                setSelectedContact(null);
                setEditedContact({
                  first_name: '',
                  last_name: '',
                  organization: '',
                  job_title: '',
                  emails: [{ email: '', type: 'personal' }],
                  phones: [{ number: '', type: 'mobile' }],
                  notes: '',
                  category: 'other'
                });
                setIsEditMode(true);
                setIsDetailModalOpen(true);
              }}
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Add Contact</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>

        {/* Mobile category bar — horizontal scroll. Desktop uses the
            vertical sidebar below. */}
        <div className="md:hidden mb-3 flex gap-1 overflow-x-auto pb-2 items-center">
          {sortedCategories.map((category) => {
            const accountContacts = (() => {
              if (activeNylasAccount === 'icloud') return categorizedContacts;
              if (activeNylasAccount === 'all') {
                let allContacts = [];
                if (icloudSynced) allContacts = [...categorizedContacts.map(c => ({ ...c, source: 'icloud' }))];
                if (nylasAccounts.length > 0) allContacts = [...allContacts, ...nylasContacts];
                return allContacts;
              }
              if (nylasAccounts.length > 0) return nylasContacts.filter(c => c.account_email === activeNylasAccount);
              return categorizedContacts;
            })();
            const categoryCount = category.id === 'all'
              ? accountContacts.length
              : accountContacts.filter(c => c.category === category.id).length;
            return (
              <button
                key={`mobile-${category.id}`}
                onClick={() => handleCategoryChange(category.id)}
                data-testid={`contact-category-${category.id}-mobile-btn`}
                className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-all flex items-center gap-1 whitespace-nowrap flex-shrink-0 ${
                  selectedCategory === category.id
                    ? `${category.color} ${category.textColor} shadow-lg`
                    : 'bg-black border border-white text-white hover:bg-white/10'
                }`}
              >
                <span className="text-xs">{category.icon}</span>
                <span>{category.name}</span>
                {category.id !== 'all' && categoryCount > 0 && (
                  <span className="opacity-75">({categoryCount})</span>
                )}
              </button>
            );
          })}
          <button
            onClick={() => setShowContactCategoryModal(true)}
            data-testid="edit-contact-categories-mobile-btn"
            className="px-2 py-1 rounded-md text-[10px] font-medium whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1 flex-shrink-0"
          >
            <Pencil size={12} />
          </button>
          <button
            onClick={rescanAllContacts}
            disabled={isRescanningContacts}
            data-testid="rescan-contacts-mobile-btn"
            className="px-2 py-1 rounded-md text-[10px] font-medium whitespace-nowrap bg-transparent border border-dashed border-emerald-600/70 text-emerald-300 hover:bg-emerald-900/30 flex items-center gap-1 flex-shrink-0 disabled:opacity-60"
          >
            {isRescanningContacts ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          </button>
          <button
            onClick={() => bulkSelectMode ? exitBulkMode() : setBulkSelectMode(true)}
            data-testid="toggle-bulk-select-mobile-btn"
            className={`px-2 py-1 rounded-md text-[10px] font-medium whitespace-nowrap flex items-center gap-1 flex-shrink-0 ${
              bulkSelectMode ? 'bg-purple-600 text-white border border-purple-400' : 'bg-transparent border border-dashed border-purple-500/70 text-purple-300'
            }`}
          >
            <CheckCircle size={12} />
          </button>
        </div>

        {/* Main two-column layout — categories on the left, contacts on the right */}
        <div className="flex gap-3 md:gap-4">
          {/* Desktop vertical category sidebar — mirrors Calendar page layout */}
          <div className="hidden md:flex flex-col gap-1.5 w-36 lg:w-40 flex-shrink-0" data-testid="contacts-category-sidebar">
            <span className="text-xs font-semibold text-gray-500 mb-1 px-2">Categories</span>
            {sortedCategories.map((category) => {
              const accountContacts = (() => {
                if (activeNylasAccount === 'icloud') return categorizedContacts;
                if (activeNylasAccount === 'all') {
                  let allContacts = [];
                  if (icloudSynced) allContacts = [...categorizedContacts.map(c => ({ ...c, source: 'icloud' }))];
                  if (nylasAccounts.length > 0) allContacts = [...allContacts, ...nylasContacts];
                  return allContacts;
                }
                if (nylasAccounts.length > 0) return nylasContacts.filter(c => c.account_email === activeNylasAccount);
                return categorizedContacts;
              })();
              const categoryCount = category.id === 'all'
                ? accountContacts.length
                : accountContacts.filter(c => c.category === category.id).length;
              const isActive = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  data-testid={`contact-category-${category.id}-btn`}
                  className={`px-2.5 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 w-full text-left ${
                    isActive
                      ? `${category.color} ${category.textColor} border-2 border-white/40`
                      : 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <span className="text-sm flex-shrink-0">{category.icon}</span>
                  <span className="truncate flex-1">{category.name}</span>
                  <span className={`text-[10px] ${isActive ? 'text-white/80' : 'text-gray-500'}`}>{categoryCount}</span>
                </button>
              );
            })}

            {/* Edit Categories Button */}
            <button
              onClick={() => setShowContactCategoryModal(true)}
              data-testid="edit-contact-categories-btn"
              className="mt-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full text-left bg-transparent border border-dashed border-gray-600 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-2"
              title="Edit Categories"
            >
              <Pencil size={12} />
              <span>Edit Categories</span>
            </button>

            {/* Re-scan All Contacts Button */}
            <button
              onClick={rescanAllContacts}
              disabled={isRescanningContacts}
              data-testid="rescan-contacts-btn"
              className="px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full text-left bg-transparent border border-dashed border-emerald-600/70 text-emerald-300 hover:bg-emerald-900/30 hover:text-emerald-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-wait"
              title="Scan every contact against the keywords on each category and move them to the matching folder"
            >
              {isRescanningContacts ? (
                <>
                  <Loader2 size={12} className="animate-spin" />
                  <span>Scanning {rescanProgress.done}/{rescanProgress.total}</span>
                </>
              ) : (
                <>
                  <RefreshCw size={12} />
                  <span>Re-scan Contacts</span>
                </>
              )}
            </button>

            {/* Export Contacts to CSV */}
            <button
              onClick={exportContactsCsv}
              disabled={isExportingCsv || !userId}
              data-testid="export-contacts-csv-btn"
              className="px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full text-left bg-transparent border border-dashed border-sky-500/70 text-sky-300 hover:bg-sky-900/30 hover:text-sky-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-wait"
              title="Download all of your contacts as a CSV file"
            >
              {isExportingCsv ? (
                <>
                  <Loader2 size={12} className="animate-spin" />
                  <span>Exporting…</span>
                </>
              ) : (
                <>
                  <Download size={12} />
                  <span>Export CSV</span>
                </>
              )}
            </button>

            {/* Bulk Select toggle */}
            <button
              onClick={() => bulkSelectMode ? exitBulkMode() : setBulkSelectMode(true)}
              data-testid="toggle-bulk-select-btn"
              className={`px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full text-left flex items-center gap-2 ${
                bulkSelectMode
                  ? 'bg-purple-600 text-white border border-purple-400'
                  : 'bg-transparent border border-dashed border-purple-500/70 text-purple-300 hover:bg-purple-900/30 hover:text-purple-200'
              }`}
              title="Select multiple contacts to move or delete in bulk"
            >
              <CheckCircle size={12} />
              <span>{bulkSelectMode ? 'Exit Select' : 'Bulk Select'}</span>
            </button>
          </div>

          {/* Right column: bulk action bar + content */}
          <div className="flex-1 min-w-0 flex flex-col">

        {/* Bulk Action Bar — only visible while bulk select mode is active */}
        {bulkSelectMode && (
          <div
            className="flex flex-wrap items-center gap-2 px-3 py-2 mt-2 bg-purple-900/30 border border-purple-500/40 rounded-lg"
            data-testid="bulk-action-bar"
          >
            <button
              onClick={() => toggleSelectAllVisible(paginatedContacts)}
              className="px-2.5 py-1 text-[11px] font-medium text-purple-200 hover:text-white bg-black/30 hover:bg-black/50 border border-purple-500/40 rounded"
              data-testid="bulk-select-all-visible-btn"
              title="Select / deselect all contacts visible on this page"
            >
              {paginatedContacts.every(c => selectedContactIds.has(getContactKey(c))) && paginatedContacts.length > 0
                ? 'Deselect Visible'
                : 'Select Visible'}
            </button>
            <span className="text-xs text-purple-200" data-testid="bulk-selected-count">
              {selectedContactIds.size} selected
            </span>
            <div className="flex-1" />
            {isBulkProcessing && (
              <span className="text-[11px] text-purple-200 flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" />
                {bulkProgress.done}/{bulkProgress.total}
              </span>
            )}
            <button
              onClick={() => setShowBulkCategoryModal(true)}
              disabled={selectedContactIds.size === 0 || isBulkProcessing}
              data-testid="bulk-change-category-btn"
              className="px-3 py-1 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 rounded flex items-center gap-1.5"
            >
              <Settings size={12} />
              Change Category
            </button>
            <button
              onClick={bulkDeleteSelected}
              disabled={selectedContactIds.size === 0 || isBulkProcessing}
              data-testid="bulk-delete-btn"
              className="px-3 py-1 text-xs font-semibold text-white bg-red-600 hover:bg-red-500 disabled:bg-gray-700 disabled:text-gray-500 rounded flex items-center gap-1.5"
            >
              <Trash2 size={12} />
              Delete
            </button>
            <button
              onClick={exitBulkMode}
              disabled={isBulkProcessing}
              data-testid="bulk-clear-btn"
              className="px-2.5 py-1 text-[11px] text-gray-300 hover:text-white hover:bg-black/30 rounded"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Content Area */}
        <div className="bg-transparent rounded-lg flex-1 overflow-y-auto">
          {/* Hidden file input for card-level photo uploads */}
          <input
            ref={cardImageInputRef}
            type="file"
            accept="image/*"
            onChange={handleCardPhotoUpload}
            className="hidden"
            data-testid="card-photo-input"
          />
          <div className={
            contactsViewMode === 'list'
              ? 'flex flex-col gap-1.5 p-2 md:p-4'
              : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-4 p-2 md:p-4'
          }>
            {paginatedContacts.map((contact, index) => {
              const category = categories.find(c => c.id === contact.category);
              // Handle both Nylas (given_name/surname) and iCloud (first_name/last_name) formats
              const displayName = contact.name || 
                `${contact.given_name || contact.first_name || ''} ${contact.surname || contact.last_name || ''}`.trim() || 
                'Unknown';
              const displayEmail = contact.email || (contact.emails && contact.emails[0]?.email) || '';
              const displayPhone = contact.phone || (contact.phones && contact.phones[0]?.number) || 
                (contact.phone_numbers && contact.phone_numbers[0]?.number) || '';
              const displayCompany = contact.company_name || contact.company || contact.organization || '';
              const displayNotes = contact.notes || '';
              const isIcloudContact = !!contact.icloud_id;
              const isNylasContact = contact.source === 'nylas';
              
              // Get initials for avatar (no default image)
              const initials = displayName
                .split(' ')
                .filter(n => n && n.length > 0)
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2) || '?';
              
              // Generate consistent color based on name
              const colors = [
                'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600',
                'bg-orange-600', 'bg-green-600', 'bg-teal-600', 
                'bg-cyan-600', 'bg-indigo-600'
              ];
              const colorIndex = (displayName && displayName.length > 0) ? displayName.charCodeAt(0) % colors.length : 0;
              // Use category color if available, otherwise fallback to name-based color
              const avatarColor = category ? category.color : colors[colorIndex];

              // ----- List view row (compact, horizontal) ---------------------
              // Renders a slim row instead of the full grid card. Keeps the
              // same click-to-open and bulk-select semantics.
              if (contactsViewMode === 'list') {
                const isSelected = bulkSelectMode && selectedContactIds.has(getContactKey(contact));
                return (
                  <div
                    key={contact.id || contact.icloud_id || `contact-list-${index}`}
                    onClick={() => {
                      if (bulkSelectMode) { toggleContactSelection(contact); return; }
                      setSelectedContact(contact);
                      setIsDetailModalOpen(true);
                    }}
                    className={`flex items-center gap-3 px-3 py-2 bg-black border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-purple-400 ring-1 ring-purple-500/60'
                        : 'border-gray-700 hover:border-gray-400 hover:bg-gray-900/40'
                    }`}
                    data-testid={`contact-row-${contact.id || index}`}
                  >
                    {bulkSelectMode && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleContactSelection(contact); }}
                        data-testid={`contact-row-checkbox-${contact.id || contact.icloud_id || index}`}
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected
                            ? 'bg-purple-600 border-purple-400 text-white'
                            : 'bg-black/70 border-gray-500 text-transparent hover:border-purple-400'
                        }`}
                        title="Toggle selection"
                      >
                        <Check size={12} />
                      </button>
                    )}
                    {/* Avatar */}
                    <div className={`w-9 h-9 rounded-full ${avatarColor} flex items-center justify-center text-white font-bold text-xs flex-shrink-0 overflow-hidden border border-white/20`}>
                      {contact.profile_image || contact.picture_url ? (
                        <img src={contact.profile_image || contact.picture_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        initials
                      )}
                    </div>
                    {/* Name + company */}
                    <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 items-center">
                      <div className="min-w-0">
                        <div className="text-sm text-white font-medium truncate">{displayName}</div>
                        {displayCompany && (
                          <div className="text-[11px] text-gray-500 truncate flex items-center gap-1">
                            <Building2 size={10} />{displayCompany}
                          </div>
                        )}
                      </div>
                      {/* Email */}
                      <div className="hidden md:block min-w-0 text-[11px] text-gray-400 truncate">
                        {displayEmail ? (
                          <span className="flex items-center gap-1"><Mail size={10} />{displayEmail}</span>
                        ) : <span className="text-gray-600">—</span>}
                      </div>
                      {/* Phone */}
                      <div className="hidden md:block min-w-0 text-[11px] text-gray-400 truncate">
                        {displayPhone ? (
                          <span className="flex items-center gap-1"><Phone size={10} />{displayPhone}</span>
                        ) : <span className="text-gray-600">—</span>}
                      </div>
                    </div>
                    {/* Category badge */}
                    {category && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${category.color} text-white flex items-center gap-1 flex-shrink-0`}>
                        <span>{category.icon}</span>
                        <span className="hidden lg:inline">{category.name}</span>
                      </span>
                    )}
                    {/* Source pill */}
                    {isNylasContact && (
                      <span className="px-1.5 py-0.5 rounded bg-purple-600/30 text-purple-200 text-[9px] font-semibold flex-shrink-0">Nylas</span>
                    )}
                    {isIcloudContact && !isNylasContact && (
                      <span className="px-1.5 py-0.5 rounded bg-sky-600/30 text-sky-200 text-[9px] font-semibold flex-shrink-0">iCloud</span>
                    )}
                  </div>
                );
              }

              return (
                <div
                  key={contact.id || contact.icloud_id || `contact-${index}`}
                  onClick={() => {
                    if (bulkSelectMode) {
                      toggleContactSelection(contact);
                      return;
                    }
                    setSelectedContact(contact);
                    setIsDetailModalOpen(true);
                  }}
                  className={`relative bg-black border rounded-xl p-5 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 cursor-pointer ${
                    bulkSelectMode && selectedContactIds.has(getContactKey(contact))
                      ? 'border-purple-400 ring-2 ring-purple-500/60'
                      : 'border-gray-400 hover:border-gray-300'
                  }`}
                  data-testid={`contact-card-${contact.id || index}`}
                >
                  {/* Bulk selection checkbox — only rendered in bulk mode */}
                  {bulkSelectMode && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleContactSelection(contact);
                      }}
                      data-testid={`contact-checkbox-${contact.id || contact.icloud_id || index}`}
                      className={`absolute top-2 left-2 w-6 h-6 rounded-md border-2 flex items-center justify-center z-10 transition-colors ${
                        selectedContactIds.has(getContactKey(contact))
                          ? 'bg-purple-600 border-purple-400 text-white'
                          : 'bg-black/70 border-gray-500 text-transparent hover:border-purple-400'
                      }`}
                      title="Toggle selection"
                    >
                      <Check size={14} />
                    </button>
                  )}
                  {/* Avatar and Category Badge */}
                  <div className="flex flex-col items-center mb-4">
                    <div
                      className="relative w-20 h-20 mb-3 group"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCardUploadTargetContact(contact);
                        cardImageInputRef.current?.click();
                      }}
                      data-testid={`contact-avatar-${contact.id || index}`}
                    >
                      <div className={`w-full h-full rounded-full ${avatarColor} flex items-center justify-center text-white font-bold text-2xl border-2 border-white/20 overflow-hidden`}>
                        {contact.profile_image || contact.picture_url ? (
                          <img src={contact.profile_image || contact.picture_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          initials
                        )}
                      </div>
                      {/* Hover overlay for photo upload */}
                      <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        {uploadingCardPhotoId === (contact.id || contact.icloud_id) ? (
                          <Loader2 className="w-5 h-5 animate-spin text-white" />
                        ) : (
                          <Camera size={18} className="text-white" />
                        )}
                      </div>
                    </div>
                    
                    {/* Name */}
                    <h3 className="text-white font-semibold text-center text-sm mb-2 line-clamp-2 min-h-[2.5rem]">
                      {displayName}
                    </h3>
                    
                    {/* Source Badge */}
                    {isNylasContact && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-600 text-white mb-3 flex items-center gap-1">
                        <svg className="w-3 h-3" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        </svg>
                        {contact.account_email?.split('@')[0] || 'Nylas'}
                      </span>
                    )}
                    
                    {/* Editable Category Badge */}
                    {!isNylasContact && (
                      <div className="relative mb-3">
                        {editingContactCardCategory === (contact.id || contact.icloud_id) ? (
                          // Category Dropdown Editor
                          <div 
                            className="absolute z-50 top-0 left-1/2 -translate-x-1/2 bg-gray-900 border border-gray-600 rounded-lg shadow-xl p-2 min-w-[160px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="text-xs text-gray-400 mb-2 px-1">Select Category</div>
                            <div className="max-h-48 overflow-y-auto space-y-1">
                              {sortedCategories.filter(c => c.id !== 'all').map((cat) => (
                                <button
                                  key={cat.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateSingleContactCategory(contact, cat.id);
                                  }}
                                  className={`w-full px-2 py-1.5 rounded text-xs font-medium flex items-center gap-2 transition-colors ${
                                    contact.category === cat.id 
                                      ? `${cat.color} ${cat.textColor}` 
                                      : 'hover:bg-gray-700 text-gray-300'
                                  }`}
                                >
                                  <span>{cat.icon}</span>
                                  <span className="truncate">{cat.name}</span>
                                  {contact.category === cat.id && <Check size={12} className="ml-auto" />}
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingContactCardCategory(null);
                              }}
                              className="w-full mt-2 px-2 py-1 text-xs text-gray-400 hover:text-white hover:bg-gray-700 rounded flex items-center justify-center gap-1"
                            >
                              <X size={12} /> Cancel
                            </button>
                          </div>
                        ) : (
                          // Display Category Badge with Edit Button
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingContactCardCategory(contact.id || contact.icloud_id);
                            }}
                            className={`group px-3 py-1 rounded-full text-xs font-semibold ${category ? `${category.color} ${category.textColor}` : 'bg-gray-700 text-gray-300'} hover:opacity-90 transition-all flex items-center gap-1`}
                            title="Click to change category"
                            data-testid={`edit-category-${contact.id || contact.icloud_id}`}
                          >
                            {category?.icon && <span>{category.icon}</span>}
                            <span>{category?.name || (isIcloudContact ? 'iCloud' : 'Uncategorized')}</span>
                            <Pencil size={10} className="opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Contact Info */}
                  <div className="space-y-2 mb-4 min-h-[4rem]">
                    {displayEmail && (
                      <div className="flex items-center gap-2 text-xs text-gray-300">
                        <Mail size={12} className="flex-shrink-0" />
                        <span className="truncate">{displayEmail}</span>
                      </div>
                    )}
                    {displayPhone && (
                      <div className="flex items-center gap-2 text-xs text-gray-300">
                        <Phone size={12} className="flex-shrink-0" />
                        <span className="truncate">{displayPhone}</span>
                      </div>
                    )}
                    {displayCompany && (
                      <div className="flex items-center gap-2 text-xs text-gray-300">
                        <Briefcase size={12} className="flex-shrink-0" />
                        <span className="truncate">{displayCompany}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Notes Section */}
                  {displayNotes && displayNotes.trim() !== '' && (
                    <div className="mb-4 pt-3 border-t border-white/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-xs font-medium">Notes</span>
                      </div>
                      <p className="text-gray-400 text-xs line-clamp-2">{displayNotes}</p>
                    </div>
                  )}
                  
                  {/* Action Icons */}
                  <div className="flex items-center justify-between pt-3 border-t border-white/20">
                    <button 
                      className="text-white hover:text-cyan-400 transition-colors p-1"
                      title={displayEmail ? `Email ${displayEmail}` : 'No email available'}
                      data-testid={`email-btn-${contact.icloud_id || index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        sendEmail(displayEmail);
                      }}
                    >
                      <Mail size={16} className={!displayEmail ? 'opacity-50' : ''} />
                    </button>
                    <button 
                      className="text-white hover:text-green-400 transition-colors p-1"
                      title={displayPhone ? `Call ${displayPhone}` : 'No phone available'}
                      data-testid={`call-btn-${contact.icloud_id || index}`}
                      disabled={callingContactId === (contact.icloud_id || contact.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        initiateCall(contact, displayPhone);
                      }}
                    >
                      {callingContactId === (contact.icloud_id || contact.id) ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Phone size={16} className={!displayPhone ? 'opacity-50' : ''} />
                      )}
                    </button>
                    <button 
                      className="text-white hover:text-cyan-400 transition-colors p-1"
                      title="View Company"
                      data-testid={`company-btn-${contact.icloud_id || index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (contact.organization) {
                          window.open(`https://www.google.com/search?q=${encodeURIComponent(contact.organization)}`, '_blank');
                        }
                      }}
                    >
                      <Building2 size={16} className={!contact.organization ? 'opacity-50' : ''} />
                    </button>
                    <button 
                      className="text-white hover:text-yellow-400 transition-colors p-1"
                      title="Edit Contact"
                      data-testid={`edit-btn-${contact.icloud_id || index}`}
                      onClick={(e) => openEditModal(contact, e)}
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="text-white hover:text-cyan-400 transition-colors p-1"
                      title="View Profile"
                      data-testid={`profile-btn-${contact.icloud_id || index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedContact(contact);
                        setIsDetailModalOpen(true);
                      }}
                    >
                      <Users size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 py-6 border-t border-gray-700">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg font-semibold transition-colors ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
              >
                Next
              </button>
              
              <span className="text-gray-400 text-sm ml-4">
                Page {currentPage} of {totalPages}
              </span>
            </div>
          )}
          </div>{/* /content area */}
          </div>{/* /right column */}
        </div>{/* /flex two-column layout */}
      </div>

      {/* Contact Detail Modal */}
      {isDetailModalOpen && (selectedContact || isEditMode) && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-8" onClick={() => { setIsDetailModalOpen(false); setIsEditMode(false); setEditedContact(null); setLinkedinImportedData(null); setLinkedinUrlInput(''); }}>
        <div className="bg-black border-2 border-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {isEditMode ? (selectedContact ? 'Edit Contact' : 'Add New Contact') : 'Contact Details'}
              </h2>
              <div className="flex items-center gap-3">
                {!isEditMode && selectedContact && (
                  <>
                    <button 
                      onClick={handleEditContact}
                      className="text-blue-400 hover:text-blue-300 px-4 py-2 border border-blue-400 rounded-lg transition-colors"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={handleDeleteContact}
                      className="text-red-400 hover:text-red-300 px-4 py-2 border border-red-400 rounded-lg transition-colors"
                    >
                      Delete
                    </button>
                  </>
                )}
                <button onClick={() => { setIsDetailModalOpen(false); setIsEditMode(false); setEditedContact(null); setLinkedinImportedData(null); setLinkedinUrlInput(''); }} className="text-gray-400 hover:text-white">
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contact Info - View Mode */}
            {!isEditMode && selectedContact && (
              <>
                <div className="space-y-6">
                  {/* Name & Avatar */}
                  <div className="flex items-center gap-4">
                    {/* Hidden file input for image upload */}
                    <input
                      ref={contactImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleContactImageUpload}
                      className="hidden"
                      data-testid="contact-image-input"
                    />
                    
                    {/* Clickable Avatar */}
                    <div 
                      onClick={() => contactImageInputRef.current?.click()}
                      className={`relative w-20 h-20 rounded-full cursor-pointer group ${(() => {
                        // Use category color if available, otherwise fallback to name-based color
                        const cat = categories.find(c => c.id === selectedContact.category);
                        if (cat) return cat.color;
                        const name = `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim();
                        const colors = ['bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600', 'bg-orange-600', 'bg-green-600', 'bg-teal-600', 'bg-cyan-600', 'bg-indigo-600'];
                        return colors[name.charCodeAt(0) % colors.length];
                      })()} flex items-center justify-center text-white font-bold text-2xl border-2 border-white/20 overflow-hidden`}
                      data-testid="contact-avatar-edit"
                    >
                      {selectedContact.profile_image ? (
                        <img src={selectedContact.profile_image} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim().split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                      )}
                      
                      {/* Hover overlay for upload */}
                      <div className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {uploadingContactImage ? (
                          <Loader2 className="w-6 h-6 animate-spin text-white" />
                        ) : (
                          <div className="text-center">
                            <Edit size={20} className="mx-auto text-white mb-1" />
                            <span className="text-xs text-white">Edit</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-2xl font-bold text-white">{`${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim()}</h3>
                      {selectedContact.organization && (
                        <p className="text-gray-400">{selectedContact.organization}</p>
                      )}
                    </div>
                  </div>

                  {/* Category Badge */}
                  {(() => {
                    const cat = categories.find(c => c.id === selectedContact.category);
                    return cat ? (
                      <div>
                        <span className={`px-4 py-2 rounded-lg ${cat.color} ${cat.textColor} text-sm font-semibold inline-flex items-center gap-2`}>
                          <span>{cat.icon}</span>
                          <span>{cat.name}</span>
                        </span>
                      </div>
                    ) : null;
                  })()}

                  {/* Emails */}
                  {selectedContact.emails && selectedContact.emails.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2">Email</h4>
                      {selectedContact.emails.map((email, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-white mb-2">
                          <Mail size={16} className="text-gray-400" />
                          <a href={`mailto:${email.email}`} className="hover:text-blue-400">
                            {email.email}
                          </a>
                          {email.type && <span className="text-xs text-gray-500">({email.type})</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Phones */}
                  {selectedContact.phones && selectedContact.phones.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2">Phone</h4>
                      {selectedContact.phones.map((phone, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-white mb-2">
                          <Phone size={16} className="text-gray-400" />
                          <a href={`tel:${phone.number}`} className="hover:text-blue-400">
                            {phone.number}
                          </a>
                          {phone.type && <span className="text-xs text-gray-500">({phone.type})</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Organization */}
                  {selectedContact.organization && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2">Organization</h4>
                      <p className="text-white">{selectedContact.organization}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedContact.notes && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2">Notes</h4>
                      <p className="text-white whitespace-pre-wrap">{selectedContact.notes}</p>
                    </div>
                  )}

                  {/* LinkedIn Profile Info (if available) */}
                  {(selectedContact.linkedin_url || selectedContact.job_title || selectedContact.location || selectedContact.education || selectedContact.company_website || selectedContact.about) && (
                    <div className="pt-4 border-t border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
                        <Linkedin size={14} className="text-blue-500" />
                        LinkedIn Info
                      </h4>
                      {selectedContact.job_title && (
                        <div className="flex items-center gap-2 text-white mb-2">
                          <Briefcase size={16} className="text-gray-400" />
                          <span>{selectedContact.job_title}</span>
                        </div>
                      )}
                      {selectedContact.location && (
                        <div className="flex items-center gap-2 text-white mb-2">
                          <MapPin size={16} className="text-gray-400" />
                          <span>{selectedContact.location}</span>
                        </div>
                      )}
                      {selectedContact.education && (
                        <div className="flex items-center gap-2 text-white mb-2">
                          <GraduationCap size={16} className="text-indigo-400" />
                          <span>{selectedContact.education}</span>
                        </div>
                      )}
                      {selectedContact.company_website && (
                        <div className="mb-2">
                          <a 
                            href={selectedContact.company_website} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center gap-2 px-3 py-2 bg-teal-600/20 hover:bg-teal-600/30 border border-teal-500/50 rounded-lg text-teal-400 hover:text-teal-300 transition-colors"
                          >
                            <Globe size={16} className="text-teal-400" />
                            <span className="text-sm truncate flex-1">{selectedContact.company_website}</span>
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      )}
                      {selectedContact.about && (
                        <div className="mt-3 p-3 bg-gray-800/50 rounded-lg">
                          <p className="text-gray-300 text-sm whitespace-pre-wrap">{selectedContact.about}</p>
                        </div>
                      )}
                      {selectedContact.linkedin_url && (
                        <div className="mb-2 mt-3">
                          <a 
                            href={selectedContact.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/50 rounded-lg text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            <Linkedin size={18} className="text-blue-500" />
                            <span className="text-sm truncate flex-1">{selectedContact.linkedin_url}</span>
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Import from LinkedIn Section */}
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="p-4 bg-gray-800/50 border border-gray-600/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-4">
                        <Linkedin size={20} className="text-blue-500" />
                        <span className="text-white font-semibold">Import from LinkedIn</span>
                      </div>
                      
                      {/* LinkedIn URL Input */}
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1 relative">
                          <Link size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={linkedinUrlInput}
                            onChange={(e) => setLinkedinUrlInput(e.target.value)}
                            placeholder="Paste LinkedIn Profile URL"
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                          />
                        </div>
                        <button
                          onClick={() => handleLinkedInUrlScan(linkedinUrlInput)}
                          disabled={isScanning || !linkedinUrlInput}
                          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center gap-2 whitespace-nowrap text-sm"
                        >
                          {isScanning ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Scanning...
                            </>
                          ) : (
                            <>
                              <Search size={14} />
                              Scan URL
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Auto Search Button */}
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400 text-sm">Don&apos;t have the URL?</span>
                        <button
                          onClick={handleAutoSearchLinkedIn}
                          disabled={isSearchingLinkedIn || isScanning}
                          className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium flex items-center gap-2"
                        >
                          {isSearchingLinkedIn ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Searching...
                            </>
                          ) : (
                            <>
                              <Search size={14} />
                              Auto-Find Profile
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Found URL Message */}
                      {linkedinImportedData && linkedinImportedData.found && (
                        <div className="mt-3 p-3 bg-green-900/30 border border-green-500/50 rounded-lg">
                          <div className="flex items-center gap-2 text-green-300 text-sm">
                            <CheckCircle size={16} />
                            <span>{linkedinImportedData.message}</span>
                          </div>
                          {linkedinImportedData.url && (
                            <a href={linkedinImportedData.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mt-1">
                              {linkedinImportedData.url}
                              <ExternalLink size={12} />
                            </a>
                          )}
                        </div>
                      )}

                      {/* LinkedIn Import Success */}
                      {linkedinImportedData && !linkedinImportedData.error && !linkedinImportedData.found && linkedinImportedData.fieldCount > 0 && (
                        <div className="mt-3 p-3 bg-blue-900/30 border border-blue-500/50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle size={16} className="text-blue-400" />
                            <span className="text-blue-300 font-semibold text-sm">✅ LinkedIn Data Found! ({linkedinImportedData.fieldCount} fields)</span>
                          </div>
                          <div className="space-y-1 text-xs">
                            {linkedinImportedData.fields.first_name && (
                              <div className="text-gray-300">
                                <span className="text-gray-500">Name:</span> {linkedinImportedData.fields.first_name} {linkedinImportedData.fields.last_name || ''}
                              </div>
                            )}
                            {linkedinImportedData.fields.job_title && (
                              <div className="text-gray-300">
                                <span className="text-gray-500">Title:</span> {linkedinImportedData.fields.job_title}
                              </div>
                            )}
                            {linkedinImportedData.fields.organization && (
                              <div className="text-gray-300">
                                <span className="text-gray-500">Company:</span> {linkedinImportedData.fields.organization}
                              </div>
                            )}
                            {linkedinImportedData.fields.location && (
                              <div className="text-gray-300">
                                <span className="text-gray-500">Location:</span> {linkedinImportedData.fields.location}
                              </div>
                            )}
                            {linkedinImportedData.fields.linkedin_url && (
                              <div className="text-gray-300">
                                <span className="text-gray-500">URL:</span> <a href={linkedinImportedData.fields.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-400">{linkedinImportedData.fields.linkedin_url}</a>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setIsEditMode(true);
                              // Data is already in editedContact from the scan
                            }}
                            className="mt-3 w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                          >
                            <CheckCircle size={14} />
                            Save to Contact
                          </button>
                        </div>
                      )}

                      {/* Error State */}
                      {linkedinImportedData && linkedinImportedData.error && (
                        <div className="mt-3 p-3 bg-red-900/30 border border-red-500/50 rounded-lg">
                          <p className="text-red-300 text-sm">{linkedinImportedData.message}</p>
                          {linkedinImportedData.searchedName && (
                            <p className="text-gray-400 text-xs mt-1">Searched: {linkedinImportedData.searchedName}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 mt-6 pt-6 border-t border-gray-700">
                  <button
                    onClick={() => selectedContact.emails?.[0] && (window.location.href = `mailto:${selectedContact.emails[0].email}`)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2"
                  >
                    <Mail size={18} />
                    Email
                  </button>
                  <button
                    onClick={() => selectedContact.phones?.[0] && (window.location.href = `tel:${selectedContact.phones[0].number}`)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2"
                  >
                    <Phone size={18} />
                    Call
                  </button>
                </div>
              </>
            )}

            {/* Contact Info - Edit Mode */}
            {isEditMode && editedContact && (
              <>
                {/* LinkedIn Import Summary - Shows in edit mode after import */}
                {linkedinImportedData && !linkedinImportedData.error && !linkedinImportedData.found && linkedinImportedData.fieldCount > 0 && (
                  <div className="mb-6 p-4 bg-blue-900/30 border border-blue-500/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Linkedin size={18} className="text-blue-400" />
                      <span className="text-blue-300 font-semibold text-sm">✅ LinkedIn data applied to form below</span>
                    </div>
                    <p className="text-blue-300 text-xs">Review the fields and click Save Changes to update contact.</p>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Name Fields */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">Name</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={editedContact.first_name || ''}
                        onChange={(e) => updateEditedField('first_name', e.target.value)}
                        placeholder="First Name"
                        className="px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                      <input
                        type="text"
                        value={editedContact.last_name || ''}
                        onChange={(e) => updateEditedField('last_name', e.target.value)}
                        placeholder="Last Name"
                        className="px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Category Selection */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">Category</h4>
                    <div className="flex flex-wrap gap-2">
                      {sortedCategories.filter(cat => cat.id !== 'all').map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => updateEditedField('category', cat.id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${
                            editedContact.category === cat.id
                              ? `${cat.color} ${cat.textColor} ring-2 ring-white/50`
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                          data-testid={`edit-contact-category-select-${cat.id}`}
                        >
                          <span>{cat.icon}</span>
                          <span>{cat.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Organization */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">Organization</h4>
                    <input
                      type="text"
                      value={editedContact.organization || ''}
                      onChange={(e) => updateEditedField('organization', e.target.value)}
                      placeholder="Company/Organization"
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  {/* Job Title */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">Job Title</h4>
                    <input
                      type="text"
                      value={editedContact.job_title || ''}
                      onChange={(e) => updateEditedField('job_title', e.target.value)}
                      placeholder="Job Title / Position"
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  {/* LinkedIn Profile URL */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                      <Linkedin size={14} className="text-blue-500" />
                      LinkedIn Profile URL
                    </h4>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editedContact.linkedin_url || ''}
                        onChange={(e) => updateEditedField('linkedin_url', e.target.value)}
                        placeholder="https://linkedin.com/in/username"
                        className="flex-1 px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                      {editedContact.linkedin_url && (
                        <a
                          href={editedContact.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                      <MapPin size={14} className="text-gray-400" />
                      Location
                    </h4>
                    <input
                      type="text"
                      value={editedContact.location || ''}
                      onChange={(e) => updateEditedField('location', e.target.value)}
                      placeholder="City, State/Country"
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  {/* Education */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                      <GraduationCap size={14} className="text-indigo-400" />
                      Education
                    </h4>
                    <input
                      type="text"
                      value={editedContact.education || ''}
                      onChange={(e) => updateEditedField('education', e.target.value)}
                      placeholder="University, Degree (e.g., Harvard - MBA)"
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  {/* Company Website */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                      <Globe size={14} className="text-teal-400" />
                      Company Website
                    </h4>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={editedContact.company_website || ''}
                        onChange={(e) => updateEditedField('company_website', e.target.value)}
                        placeholder="https://company.com"
                        className="flex-1 px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                      {editedContact.company_website && (
                        <a
                          href={editedContact.company_website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-1"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* About / Bio */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">About / Bio</h4>
                    <textarea
                      value={editedContact.about || ''}
                      onChange={(e) => updateEditedField('about', e.target.value)}
                      placeholder="Professional summary or bio from LinkedIn..."
                      rows={3}
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
                    />
                  </div>

                  {/* Emails */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-400">Email Addresses</h4>
                      <button
                        onClick={addEmail}
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                      >
                        <Plus size={14} />
                        Add Email
                      </button>
                    </div>
                    {editedContact.emails?.map((email, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2">
                        <input
                          type="email"
                          value={email.email || ''}
                          onChange={(e) => updateEditedEmail(idx, e.target.value)}
                          placeholder="email@example.com"
                          className="flex-1 px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                        />
                        <button
                          onClick={() => removeEmail(idx)}
                          className="text-red-400 hover:text-red-300 p-2"
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Phones */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-400">Phone Numbers</h4>
                      <button
                        onClick={addPhone}
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                      >
                        <Plus size={14} />
                        Add Phone
                      </button>
                    </div>
                    {editedContact.phones?.map((phone, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2">
                        <input
                          type="tel"
                          value={phone.number || ''}
                          onChange={(e) => updateEditedPhone(idx, e.target.value)}
                          placeholder="+1 234 567 8900"
                          className="flex-1 px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                        />
                        <button
                          onClick={() => removePhone(idx)}
                          className="text-red-400 hover:text-red-300 p-2"
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Notes */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">Notes</h4>
                    <textarea
                      value={editedContact.notes || ''}
                      onChange={(e) => updateEditedField('notes', e.target.value)}
                      placeholder="Add notes about this contact..."
                      rows={4}
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
                    />
                  </div>
                </div>

                {/* Edit Actions */}
                <div className="flex gap-3 mt-6 pt-6 border-t border-gray-700">
                  <button
                    onClick={handleCancelEdit}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveContact}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold"
                  >
                    Save Changes
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )}

    {/* iCloud Disconnect Confirmation Modal */}
    {showDisconnectIcloudModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <svg className="w-6 h-6 text-red-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Disconnect iCloud</h3>
                <p className="text-sm text-gray-400">Remove all iCloud contacts</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
              <p className="text-red-300 text-sm">
                <strong>Warning:</strong> This will remove all {icloudContactsCount} iCloud contacts from this app. 
                Your contacts will remain safe in your iCloud account.
              </p>
            </div>
            <p className="text-gray-300 text-sm mb-4">
              You can reconnect at any time by entering your Apple ID and App Password again.
            </p>
          </div>
          <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
            <button
              onClick={() => setShowDisconnectIcloudModal(false)}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              disabled={disconnectingIcloud}
            >
              Cancel
            </button>
            <button
              onClick={handleDisconnectIcloud}
              disabled={disconnectingIcloud}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
              data-testid="confirm-disconnect-icloud"
            >
              {disconnectingIcloud ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Disconnecting...
                </>
              ) : (
                <>
                  <X size={16} />
                  Disconnect iCloud
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* iCloud Credentials Modal */}
    {showCredentialsModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Connect iCloud</h3>
                <p className="text-sm text-gray-400">Sign in to import your contacts</p>
              </div>
            </div>
          </div>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            if (icloudEmail && icloudAppPassword) {
              authenticateAndSync(icloudEmail, icloudAppPassword);
            }
          }} className="p-6 space-y-4">
            {credentialError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {credentialError}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Apple ID (Email)</label>
              <input
                type="email"
                value={icloudEmail}
                onChange={(e) => setIcloudEmail(e.target.value)}
                placeholder="your@icloud.com"
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                required
                data-testid="icloud-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">App-Specific Password</label>
              <input
                type="password"
                value={icloudAppPassword}
                onChange={(e) => setIcloudAppPassword(e.target.value)}
                placeholder="xxxx-xxxx-xxxx-xxxx"
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                required
                data-testid="icloud-password-input"
              />
              <p className="mt-2 text-xs text-gray-500">
                Generate an app-specific password at{' '}
                <a 
                  href="https://appleid.apple.com/account/manage" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  appleid.apple.com
                </a>
                {' '}→ Sign-In & Security → App-Specific Passwords
              </p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowCredentialsModal(false);
                  setCredentialError('');
                }}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={authenticating || !icloudEmail || !icloudAppPassword}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="icloud-connect-submit"
              >
                {authenticating ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Cloud size={18} />
                    Connect
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Google Contacts Connection Modal */}
    {showGoogleContactsModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowGoogleContactsModal(false)}>
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-white rounded-lg">
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Connect Google Contacts</h3>
                <p className="text-sm text-gray-400">Import your Google contacts</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-3">How to Connect:</h4>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
                  <p>Click "Connect with Google" below</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">2</span>
                  <p>Sign in to your Google account</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">3</span>
                  <p>Allow access to your contacts</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">4</span>
                  <p>Your contacts will be imported automatically</p>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-xs text-blue-400">
                <strong>Note:</strong> Google Contacts uses OAuth for secure authentication. Your credentials are never stored on our servers.
              </p>
            </div>
          </div>
          
          <div className="p-6 border-t border-gray-700 flex gap-3">
            <button
              onClick={() => setShowGoogleContactsModal(false)}
              className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                window.open('https://contacts.google.com/', '_blank');
                setShowGoogleContactsModal(false);
              }}
              className="flex-1 px-4 py-3 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Connect with Google
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Outlook Contacts Connection Modal */}
    {showOutlookContactsModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowOutlookContactsModal(false)}>
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#0078D4] rounded-lg">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Connect Outlook Contacts</h3>
                <p className="text-sm text-gray-400">Import your Outlook/Microsoft contacts</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-3">How to Connect:</h4>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
                  <p>Click "Connect with Outlook" below</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">2</span>
                  <p>Sign in to your Microsoft account</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">3</span>
                  <p>Allow access to your contacts</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">4</span>
                  <p>Your contacts will be synced to your dashboard</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-2">Or Sync from Desktop:</h4>
              <p className="text-sm text-gray-400">
                You can also sync contacts from Outlook Desktop on your Mac using the Shortcuts automation app.
              </p>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-xs text-blue-400">
                <strong>Note:</strong> Outlook uses OAuth for secure authentication. Your credentials are never stored on our servers.
              </p>
            </div>
          </div>
          
          <div className="p-6 border-t border-gray-700 flex gap-3">
            <button
              onClick={() => setShowOutlookContactsModal(false)}
              className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                window.open('https://outlook.live.com/people/', '_blank');
                setShowOutlookContactsModal(false);
              }}
              className="flex-1 px-4 py-3 bg-[#0078D4] hover:bg-[#106EBE] text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
              </svg>
              Connect with Outlook
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Add Contact Account Modal */}
    {showAddContactAccountModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddContactAccountModal(false)}>
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-xl font-bold text-white mb-1">Add Contact Account</h3>
            <p className="text-sm text-gray-400">Connect a new contact source to your dashboard</p>
          </div>
          
          <div className="p-6 space-y-4">
            {addContactAccountError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {addContactAccountError}
              </div>
            )}
            
            {/* Tab Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tab Name</label>
              <input
                type="text"
                value={newContactAccountName}
                onChange={(e) => setNewContactAccountName(e.target.value)}
                placeholder="e.g., Work, Personal, Family"
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                data-testid="new-contact-account-name"
              />
              <p className="mt-1 text-xs text-gray-500">This name will appear as the tab label</p>
            </div>
            
            {/* Contact Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Contact Provider</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setNewContactAccountType('icloud')}
                  className={`p-3 rounded-lg border transition-all flex flex-col items-center gap-2 ${
                    newContactAccountType === 'icloud'
                      ? 'bg-blue-600/20 border-blue-500 text-blue-400'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span className="text-xs font-medium">iCloud</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewContactAccountType('google')}
                  className={`p-3 rounded-lg border transition-all flex flex-col items-center gap-2 ${
                    newContactAccountType === 'google'
                      ? 'bg-red-600/20 border-red-500 text-red-400'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  </svg>
                  <span className="text-xs font-medium">Google</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewContactAccountType('outlook')}
                  className={`p-3 rounded-lg border transition-all flex flex-col items-center gap-2 ${
                    newContactAccountType === 'outlook'
                      ? 'bg-blue-600/20 border-[#0078D4] text-[#0078D4]'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229z"/>
                  </svg>
                  <span className="text-xs font-medium">Outlook</span>
                </button>
              </div>
            </div>
            
            {/* Credentials Section - Show based on provider type */}
            {newContactAccountType === 'icloud' && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  iCloud Credentials
                </h4>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Apple ID (Email)</label>
                  <input
                    type="email"
                    value={newContactAccountEmail}
                    onChange={(e) => setNewContactAccountEmail(e.target.value)}
                    placeholder="your@icloud.com"
                    className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">App-Specific Password</label>
                  <input
                    type="password"
                    value={newContactAccountPassword}
                    onChange={(e) => setNewContactAccountPassword(e.target.value)}
                    placeholder="xxxx-xxxx-xxxx-xxxx"
                    className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Generate at{' '}
                    <a 
                      href="https://appleid.apple.com/account/manage" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline"
                    >
                      appleid.apple.com
                    </a>
                  </p>
                </div>
              </div>
            )}
            
            {newContactAccountType === 'google' && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <h4 className="text-white font-semibold flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#EA4335">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  </svg>
                  Google Contacts
                </h4>
                <p className="text-sm text-gray-400 mb-3">
                  Google uses OAuth for secure authentication. After adding, click the connect button to sign in with Google.
                </p>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2">
                  <p className="text-xs text-green-400">
                    ✓ No password needed - Uses secure Google sign-in
                  </p>
                </div>
              </div>
            )}
            
            {newContactAccountType === 'outlook' && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <h4 className="text-white font-semibold flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-[#0078D4]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229z"/>
                  </svg>
                  Outlook Contacts
                </h4>
                <p className="text-sm text-gray-400 mb-3">
                  Outlook uses OAuth for secure authentication. After adding, click the connect button to sign in with Microsoft.
                </p>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2">
                  <p className="text-xs text-green-400">
                    ✓ No password needed - Uses secure Microsoft sign-in
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div className="p-6 border-t border-gray-700 flex gap-3">
            <button
              onClick={() => {
                setShowAddContactAccountModal(false);
                setNewContactAccountName('');
                setNewContactAccountEmail('');
                setNewContactAccountType('icloud');
                setNewContactAccountPassword('');
                setAddContactAccountError('');
              }}
              className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddContactAccount}
              className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="add-contact-account-submit"
            >
              <Plus size={18} />
              Add Account
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* Contact Category Management Modal */}
    {showContactCategoryModal && (
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={() => setShowContactCategoryModal(false)}
      >
        <div 
          className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">Edit Contact Categories</h3>
            <button
              onClick={() => setShowContactCategoryModal(false)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={18} className="text-gray-400" />
            </button>
          </div>
          
          {/* Add New Category */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex gap-2">
              {/* Icon Selector */}
              <div className="relative">
                <button
                  type="button"
                  className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center hover:border-gray-500"
                  onClick={(e) => {
                    const dropdown = e.currentTarget.nextElementSibling;
                    dropdown.classList.toggle('hidden');
                  }}
                >
                  {newContactCategoryIcon}
                </button>
                <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-6 gap-1 w-48">
                  {categoryIcons.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={(e) => {
                        setNewContactCategoryIcon(icon);
                        e.currentTarget.parentElement.classList.add('hidden');
                      }}
                      className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center"
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <input
                type="text"
                value={newContactCategoryName}
                onChange={(e) => setNewContactCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addContactCategory()}
                placeholder="New category name..."
                className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                data-testid="new-contact-category-input"
              />
              <button
                onClick={addContactCategory}
                disabled={!newContactCategoryName.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-1.5"
                data-testid="add-contact-category-btn"
              >
                <Plus size={14} />
                Add
              </button>
            </div>
          </div>
          
          {/* Category List - Draggable */}
          <div className="p-4 max-h-80 overflow-y-auto">
            <p className="text-xs text-gray-500 mb-3">Drag to reorder categories</p>
            <DragDropContext
              onDragEnd={(result) => {
                if (!result.destination) return;
                
                // Get non-all categories for reordering
                const nonAllCategories = categories.filter(c => c.id !== 'all');
                const allCategory = categories.find(c => c.id === 'all');
                
                // Reorder within the non-all list
                const items = Array.from(nonAllCategories);
                const [reorderedItem] = items.splice(result.source.index, 1);
                items.splice(result.destination.index, 0, reorderedItem);
                
                // Reconstruct with 'all' at the beginning
                const newCategories = allCategory ? [allCategory, ...items] : items;
                setCustomContactCategories(newCategories);
                toast.success('Category order saved');
              }}
            >
              <Droppable droppableId="contact-categories">
                {(provided) => (
                  <div 
                    {...provided.droppableProps} 
                    ref={provided.innerRef}
                    className="space-y-2"
                  >
                    {categories.filter(c => c.id !== 'all').map((category, index) => {
                      const protectedCategories = ['passwords', 'real-estate', 'family', 'other'];
                      const isProtected = protectedCategories.includes(category.id);
                      
                      return (
                        <Draggable 
                          key={category.id} 
                          draggableId={category.id} 
                          index={index}
                          isDragDisabled={editingContactCategory === category.id}
                        >
                          {(provided, snapshot) => (
                            <div 
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex flex-col gap-1.5 p-2 bg-gray-800/50 rounded-lg group ${
                                snapshot.isDragging ? 'shadow-lg ring-2 ring-purple-500' : ''
                              }`}
                            >
                            <div className="flex items-center gap-2">
                              {/* Drag Handle */}
                              <div 
                                {...provided.dragHandleProps}
                                className="cursor-grab active:cursor-grabbing p-1 text-gray-500 hover:text-gray-300"
                              >
                                <GripVertical size={16} />
                              </div>
                              
                              {editingContactCategory === category.id ? (
                                <>
                                  <span className="text-lg">{category.icon}</span>
                                  <input
                                    type="text"
                                    value={editContactCategoryValue}
                                    onChange={(e) => setEditContactCategoryValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') updateContactCategory(category.id, editContactCategoryValue, category.icon);
                                      if (e.key === 'Escape') {
                                        cancelEditingContactCategory();
                                      }
                                    }}
                                    className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                                    autoFocus
                                    data-testid={`edit-contact-category-input-${category.id}`}
                                  />
                                  <button
                                    onClick={() => updateContactCategory(category.id, editContactCategoryValue, category.icon)}
                                    className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                                    title="Save"
                                  >
                                    <Check size={14} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      cancelEditingContactCategory();
                                    }}
                                    className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                                    title="Cancel"
                                  >
                                    <X size={14} />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span className={`w-8 h-8 rounded-lg ${category.color} flex items-center justify-center text-sm`}>
                                    {category.icon}
                                  </span>
                                  <span className="flex-1 text-white text-sm">
                                    {category.name}
                                    {isProtected && (
                                      <span className="ml-2 text-xs text-gray-500">(default)</span>
                                    )}
                                  </span>
                                  {/* Keywords toggle — opens an inline editor below this row */}
                                  <button
                                    onClick={() => {
                                      if (editingKeywordsFor === category.id) {
                                        setEditingKeywordsFor(null);
                                      } else {
                                        openKeywordEditor(category.id);
                                      }
                                    }}
                                    className={`px-2 py-1 text-[11px] rounded transition-colors flex items-center gap-1 ${
                                      editingKeywordsFor === category.id
                                        ? 'bg-emerald-500/20 text-emerald-300'
                                        : 'text-gray-400 hover:text-emerald-300 hover:bg-emerald-500/10'
                                    }`}
                                    title="Edit keywords used to auto-file contacts into this category"
                                    data-testid={`edit-keywords-btn-${category.id}`}
                                  >
                                    <Search size={12} />
                                    <span>Keywords ({Array.isArray(category.keywords) ? category.keywords.length : 0})</span>
                                  </button>
                                  <button
                                    onClick={() => {
                                      startEditingContactCategory(category.id);
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Edit"
                                    data-testid={`edit-contact-category-btn-${category.id}`}
                                  >
                                    <Pencil size={14} />
                                  </button>
                                  {!isProtected && (
                                    <button
                                      onClick={() => deleteContactCategory(category.id)}
                                      className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                      title="Delete"
                                      data-testid={`delete-contact-category-btn-${category.id}`}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>

                            {/* Inline keywords editor */}
                            {editingKeywordsFor === category.id && (
                              <div className="ml-7 mt-1 p-2 bg-black/40 rounded border border-emerald-800/40" data-testid={`keywords-editor-${category.id}`}>
                                <label className="block text-[10px] uppercase tracking-wider text-emerald-400 mb-1">
                                  Keywords for "{category.name}"
                                </label>
                                <textarea
                                  value={editingKeywordsValue}
                                  onChange={(e) => setEditingKeywordsValue(e.target.value)}
                                  rows={2}
                                  placeholder="e.g. doctor, hospital, clinic, dental"
                                  className="w-full px-2 py-1.5 bg-black border border-gray-600 rounded text-white text-xs focus:border-emerald-500 focus:outline-none resize-y"
                                  data-testid={`keywords-input-${category.id}`}
                                />
                                <p className="text-[10px] text-gray-500 mt-1">
                                  Comma-separated. Matched (case-insensitive) against contact name, organization, job title, emails and notes.
                                </p>
                                <div className="flex justify-end gap-1 mt-1.5">
                                  <button
                                    onClick={() => { setEditingKeywordsFor(null); setEditingKeywordsValue(''); }}
                                    className="px-2 py-1 text-[11px] text-gray-300 hover:text-white hover:bg-gray-700 rounded"
                                    data-testid={`keywords-cancel-${category.id}`}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => saveCategoryKeywords(category.id)}
                                    className="px-2 py-1 text-[11px] bg-emerald-600 hover:bg-emerald-500 text-white rounded flex items-center gap-1"
                                    data-testid={`keywords-save-${category.id}`}
                                  >
                                    <Check size={11} /> Save
                                  </button>
                                </div>
                              </div>
                            )}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          
          {/* Modal Footer */}
          <div className="p-4 border-t border-gray-700">
            <button
              onClick={() => setShowContactCategoryModal(false)}
              className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
              data-testid="close-contact-category-modal-btn"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Bulk Change Category Modal */}
    {showBulkCategoryModal && (
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={() => !isBulkProcessing && setShowBulkCategoryModal(false)}
        data-testid="bulk-category-modal"
      >
        <div
          className="bg-gray-900 rounded-2xl w-full max-w-md border border-purple-500/40 shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">
              Move {selectedContactIds.size} contact{selectedContactIds.size === 1 ? '' : 's'} to…
            </h3>
            <button
              onClick={() => !isBulkProcessing && setShowBulkCategoryModal(false)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              disabled={isBulkProcessing}
            >
              <X size={18} className="text-gray-400" />
            </button>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              {sortedCategories.filter(c => c.id !== 'all').map(cat => (
                <button
                  key={cat.id}
                  onClick={() => bulkChangeCategory(cat.id)}
                  disabled={isBulkProcessing}
                  data-testid={`bulk-category-option-${cat.id}`}
                  className="flex items-center gap-2 px-3 py-2.5 bg-gray-800/70 hover:bg-gray-700 disabled:opacity-50 rounded-lg text-left transition-colors"
                >
                  <span className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center text-sm flex-shrink-0`}>
                    {cat.icon}
                  </span>
                  <span className="text-sm text-white truncate">{cat.name}</span>
                </button>
              ))}
            </div>
            {isBulkProcessing && (
              <div className="mt-3 text-xs text-purple-200 flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" />
                Moving {bulkProgress.done} of {bulkProgress.total}…
              </div>
            )}
          </div>
        </div>
      </div>
    )}


    {/* Manage Accounts Modal */}
    {showManageAccountsModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          {/* Modal Header */}
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">Connected Accounts</h3>
            <button
              onClick={() => setShowManageAccountsModal(false)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-400" />
            </button>
          </div>
          
          {/* Modal Content */}
          <div className="p-4 overflow-y-auto flex-1 space-y-3">
            {/* Nylas Accounts */}
            {nylasAccounts.map((account) => (
              <div key={account.email} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                <div className="flex items-center gap-3">
                  {account.provider === 'google' ? (
                    <svg className="w-8 h-8" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  ) : (
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="#0078D4">
                      <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                    </svg>
                  )}
                  <div>
                    <p className="text-white font-medium">{account.account_name || account.email.split('@')[0]}</p>
                    <p className="text-gray-400 text-xs">{account.email}</p>
                    <p className="text-gray-500 text-xs">{nylasContacts.filter(c => c.account_email === account.email).length} contacts</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowManageAccountsModal(false);
                    handleDeleteContactAccount(account);
                  }}
                  className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm transition-colors"
                >
                  Disconnect
                </button>
              </div>
            ))}
            
            {/* No accounts message */}
            {nylasAccounts.length === 0 && (
              <div className="text-center py-8">
                <Users size={48} className="mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400">No accounts connected</p>
                <p className="text-gray-500 text-sm mt-1">Connect an account to sync your contacts</p>
              </div>
            )}
          </div>
          
          {/* Modal Footer */}
          <div className="p-4 border-t border-gray-700">
            <button
              onClick={handleConnectContactAccount}
              className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
            >
              <Plus size={16} />
              Connect New Account
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Delete Contact Account Confirmation Modal */}
    {showDeleteContactAccountConfirm && contactAccountToDelete && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-md mx-4">
          <h3 className="text-xl font-bold text-white mb-2">Disconnect Account?</h3>
          <p className="text-gray-400 mb-4">
            Are you sure you want to disconnect <strong className="text-white">{contactAccountToDelete.account_name || contactAccountToDelete.email}</strong>?
            <span className="block mt-2 text-sm">This will remove all contacts synced from this account.</span>
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowDeleteContactAccountConfirm(false);
                setContactAccountToDelete(null);
              }}
              className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              disabled={deletingContactAccount}
            >
              Cancel
            </button>
            <button
              onClick={confirmDeleteContactAccount}
              disabled={deletingContactAccount}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {deletingContactAccount ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Disconnecting...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  Disconnect
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )}
        </>
      )}

      {/* Edit Lead Company Modal */}
      {showEditCompanyModal && editingCompany && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Edit Company</h2>
              <button
                onClick={() => {
                  setShowEditCompanyModal(false);
                  setEditingCompany(null);
                }}
                className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Company Name */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Company Name</label>
                <input
                  type="text"
                  value={editCompanyForm.name}
                  onChange={(e) => setEditCompanyForm({ ...editCompanyForm, name: e.target.value })}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  placeholder="Company Name"
                />
              </div>
              
              {/* Website & Industry */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Website</label>
                  <input
                    type="text"
                    value={editCompanyForm.website}
                    onChange={(e) => setEditCompanyForm({ ...editCompanyForm, website: e.target.value })}
                    className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                    placeholder="www.company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Industry/Category</label>
                  <select
                    value={editCompanyForm.industry}
                    onChange={(e) => setEditCompanyForm({ ...editCompanyForm, industry: e.target.value })}
                    className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none appearance-none cursor-pointer"
                  >
                    <option value="">Select Category</option>
                    {leadCategories
                      .filter(cat => cat.id !== 'all') // Exclude 'All' from options
                      .map(cat => (
                        <option key={cat.id} value={cat.label}>
                          {cat.label}
                        </option>
                      ))
                    }
                  </select>
                </div>
              </div>
              
              {/* Location */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Location</label>
                <input
                  type="text"
                  value={editCompanyForm.location}
                  onChange={(e) => setEditCompanyForm({ ...editCompanyForm, location: e.target.value })}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  placeholder="City, State"
                />
              </div>
              
              {/* Primary Contact Section */}
              <div className="pt-4 border-t border-gray-700">
                <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Users size={16} className="text-cyan-400" />
                  Primary Contact
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">First Name</label>
                    <input
                      type="text"
                      value={editCompanyForm.contact_first_name}
                      onChange={(e) => setEditCompanyForm({ ...editCompanyForm, contact_first_name: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={editCompanyForm.contact_last_name}
                      onChange={(e) => setEditCompanyForm({ ...editCompanyForm, contact_last_name: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                      placeholder="Smith"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Email</label>
                    <input
                      type="email"
                      value={editCompanyForm.contact_email}
                      onChange={(e) => setEditCompanyForm({ ...editCompanyForm, contact_email: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={editCompanyForm.contact_phone}
                      onChange={(e) => setEditCompanyForm({ ...editCompanyForm, contact_phone: e.target.value })}
                      className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm text-gray-400 mb-2">Job Title</label>
                  <input
                    type="text"
                    value={editCompanyForm.contact_title}
                    onChange={(e) => setEditCompanyForm({ ...editCompanyForm, contact_title: e.target.value })}
                    className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                    placeholder="CEO, Manager, etc."
                  />
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Notes</label>
                <textarea
                  value={editCompanyForm.notes}
                  onChange={(e) => setEditCompanyForm({ ...editCompanyForm, notes: e.target.value })}
                  className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none resize-none"
                  rows={3}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditCompanyModal(false);
                  setEditingCompany(null);
                }}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLeadCompany}
                disabled={savingCompany}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {savingCompany ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Lead Categories Modal */}
      {showManageLeadCategories && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Settings size={20} className="text-cyan-400" />
                Manage Category Tabs
              </h2>
              <button
                onClick={() => {
                  setShowManageLeadCategories(false);
                  setEditingLeadCategory(null);
                  setNewLeadCategoryName('');
                  setNewLeadCategoryKeywords('');
                }}
                className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Add New Category */}
              <div className="bg-black/50 border border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3">
                  {editingLeadCategory ? 'Edit Category' : 'Add New Category'}
                </h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newLeadCategoryName}
                    onChange={(e) => setNewLeadCategoryName(e.target.value)}
                    placeholder="Category name"
                    className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  />
                  <input
                    type="text"
                    value={newLeadCategoryKeywords}
                    onChange={(e) => setNewLeadCategoryKeywords(e.target.value)}
                    placeholder="Keywords (comma separated)"
                    className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400">Color:</label>
                    <select
                      value={newLeadCategoryColor}
                      onChange={(e) => setNewLeadCategoryColor(e.target.value)}
                      className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 outline-none"
                    >
                      <option value="bg-blue-500">Blue</option>
                      <option value="bg-green-500">Green</option>
                      <option value="bg-yellow-500">Yellow</option>
                      <option value="bg-purple-500">Purple</option>
                      <option value="bg-pink-500">Pink</option>
                      <option value="bg-orange-500">Orange</option>
                      <option value="bg-cyan-500">Cyan</option>
                      <option value="bg-red-500">Red</option>
                      <option value="bg-indigo-500">Indigo</option>
                      <option value="bg-gray-600">Gray</option>
                    </select>
                    <div className={`w-8 h-8 rounded-lg ${newLeadCategoryColor}`}></div>
                  </div>
                  <div className="flex gap-2">
                    {editingLeadCategory ? (
                      <>
                        <button
                          onClick={handleUpdateLeadCategory}
                          className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
                        >
                          Update
                        </button>
                        <button
                          onClick={() => {
                            setEditingLeadCategory(null);
                            setNewLeadCategoryName('');
                            setNewLeadCategoryKeywords('');
                            setNewLeadCategoryColor('bg-cyan-600');
                          }}
                          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleAddLeadCategory}
                        className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Plus size={16} />
                        Add Category
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Existing Categories List */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-300">Current Categories</h3>
                {leadCategories.filter(c => c.id !== 'all').map((cat, idx) => (
                  <div
                    key={cat.id}
                    className="flex items-center gap-3 p-3 bg-black/50 border border-gray-700 rounded-lg"
                  >
                    <div className={`w-4 h-4 rounded ${cat.color || 'bg-gray-600'}`}></div>
                    <span className="flex-1 text-white">{cat.label || cat.name}</span>
                    {cat.keywords?.length > 0 && (
                      <span className="text-xs text-gray-500">
                        {cat.keywords.slice(0, 2).join(', ')}{cat.keywords.length > 2 ? '...' : ''}
                      </span>
                    )}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingLeadCategory(cat);
                          setNewLeadCategoryName(cat.label || cat.name);
                          setNewLeadCategoryColor(cat.color || 'bg-cyan-600');
                          setNewLeadCategoryKeywords((cat.keywords || []).join(', '));
                        }}
                        className="p-1.5 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      {!cat.isDefault && cat.id !== 'other' && (
                        <button
                          onClick={() => handleDeleteLeadCategory(cat.id)}
                          className="p-1.5 hover:bg-red-900/50 rounded text-gray-400 hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Changes sync automatically to the Lead Generation page
              </p>
            </div>
          </div>
        </div>
      )}
  </div>
);
};

export default RealEstateManagingPartnerContacts;
